import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Tag, Printer, Shield, Cpu, Wifi, Package, Clock, AlertTriangle, DollarSign, Plus, Pencil, Trash2, X, Check, Search, ExternalLink, TrendingUp, Terminal, Play, RotateCcw, XCircle, ChevronDown, Monitor, HardDrive, Activity, ThermometerSun, BatteryLow, Database, Key } from 'lucide-react';
import './AssetDetail.css';
import { incidentApi } from '../services/incidentApi';

// ── Constantes de costos ──────────────────────────────────────────────────────
const COST_CATEGORIES = [
  { value: 'license',        label: '📄 Licencia',        color: 'var(--accent)' },
  { value: 'infrastructure', label: '☁️ Infraestructura', color: 'var(--ink-2)' },
  { value: 'support',        label: '🔧 Soporte',          color: 'var(--ink-2)' },
  { value: 'saas',           label: '🌐 SaaS',             color: 'var(--green)' },
  { value: 'development',    label: '💻 Desarrollo',       color: '#D45D00' },
  { value: 'hardware',       label: '🖥 Hardware',         color: 'var(--ink-2)' },
  { value: 'other',          label: '📦 Otro',             color: 'var(--ink-3)' },
];

const FREQ_LABELS = {
  monthly:  { label: 'Mensual',  mult: 1 },
  annual:   { label: 'Anual',    mult: 1/12 },
  one_time: { label: 'Único',    mult: 0 },
};

const CURRENCIES = ['USD','ARS','EUR','BRL','CLP','COP','MXN'];

function fmtMoney(amount, currency = 'USD') {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

// ── Lookup de nombre comercial y precio de mercado ────────────────────────────
const LENOVO_ENDPOINTS = [
  (mtm) => `https://pcsupport.lenovo.com/us/en/api/v4/pcpProducts?idType=1&id=${mtm}`,
  (mtm) => `https://pcsupport.lenovo.com/us/en/api/v4/Search/Web?query=${mtm}&start=0&rows=5`,
];

async function lookupLenovoName(productNumber) {
  for (const ep of LENOVO_ENDPOINTS) {
    try {
      const res = await fetch(ep(productNumber), {
        headers: { 'Accept': 'application/json' },
        signal: AbortSignal.timeout(8000),
      });
      if (!res.ok) continue;
      const data = await res.json();
      // Intentar extraer nombre del producto de diferentes estructuras
      const name =
        data?.data?.productName ||
        data?.data?.[0]?.productName ||
        data?.hits?.[0]?.productName ||
        data?.result?.[0]?.title ||
        data?.hits?.[0]?.title ||
        null;
      if (name) return name;
    } catch (_) { continue; }
  }
  return null;
}

function hasRole(user, role) {
  return user?.role === role || (user?.extra_roles || []).includes(role);
}

// ── Tab USB ───────────────────────────────────────────────────────────────────
const API_URL_USB = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const USB_STATUS = {
  approved: { label: 'Aprobado',      color: 'var(--green)', bg: 'var(--green-dim)', icon: '✅' },
  graylist: { label: 'Lista gris',    color: 'var(--amber)', bg: 'var(--amber-dim)', icon: '⚠️' },
  blocked:  { label: 'Bloqueado',     color: 'var(--red)', bg: 'var(--red-dim)', icon: '🚫' },
  unknown:  { label: 'No registrado', color: 'var(--ink-2)', bg: 'var(--bg-subtle)', icon: '❓' },
};

// ── Tags del equipo (asignar/quitar manual) ──────────────────────────────────
const TAG_COLORS = { slate: 'var(--ink-2)', blue: 'var(--accent)', green: 'var(--green)', red: 'var(--red)', amber: 'var(--amber)', violet: 'var(--ink-2)', teal: '#0d9488', gray: 'var(--ink-2)' };
function TagsSection({ assetId }) {
  const [tags, setTags] = useState([]);
  const [all, setAll] = useState([]);
  const [adding, setAdding] = useState(false);
  const base = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(() => {
    fetch(`${base}/automation/assets/${assetId}/tags`, { headers })
      .then(r => r.ok ? r.json() : []).then(d => setTags(Array.isArray(d) ? d : [])).catch(() => {});
  }, [assetId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    fetch(`${base}/automation/tags`, { headers })
      .then(r => r.ok ? r.json() : []).then(d => { setAll(Array.isArray(d) ? d : []); setAdding(true); }).catch(() => setAdding(true));
  };
  const add = async (tagId) => {
    if (tagId) await fetch(`${base}/automation/assets/${assetId}/tags`, { method: 'POST', headers, body: JSON.stringify({ tag_id: tagId }) });
    setAdding(false); load();
  };
  const createAndAdd = async () => {
    const name = window.prompt('Nombre del nuevo tag:'); if (!name || !name.trim()) { setAdding(false); return; }
    try {
      const r = await fetch(`${base}/automation/tags`, { method: 'POST', headers, body: JSON.stringify({ name: name.trim() }) });
      const t = await r.json();
      if (t && t.id) await fetch(`${base}/automation/assets/${assetId}/tags`, { method: 'POST', headers, body: JSON.stringify({ tag_id: t.id }) });
    } catch { /* noop */ }
    setAdding(false); load();
  };
  const remove = async (tagId) => {
    await fetch(`${base}/automation/assets/${assetId}/tags/${tagId}`, { method: 'DELETE', headers });
    load();
  };

  const assignedIds = new Set(tags.map(t => t.id));
  const available = all.filter(t => !assignedIds.has(t.id));

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {tags.map(t => (
        <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--gray-100, var(--bg))', border: '1px solid var(--border, var(--border))', borderRadius: 8, padding: '5px 9px', fontSize: 12, color: 'var(--ink-2, var(--ink-2))' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: TAG_COLORS[t.color] || 'var(--ink-2)' }} />
          {t.name}
          <button onClick={() => remove(t.id)} title="Quitar tag" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 0, display: 'inline-flex' }}><X size={12} /></button>
        </span>
      ))}
      {adding ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <select autoFocus defaultValue="" onChange={e => (e.target.value === '__new' ? createAndAdd() : add(e.target.value))}
            style={{ fontSize: 12, padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border, var(--border-2))', background: 'var(--surface, #fff)', color: 'var(--ink, var(--ink))' }}>
            <option value="">Elegir tag…</option>
            {available.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            <option value="__new">＋ Crear nuevo…</option>
          </select>
          <button onClick={() => setAdding(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)' }}><X size={14} /></button>
        </span>
      ) : (
        <button onClick={openAdd} className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '5px 10px' }}>
          <Tag size={13} /> Tag
        </button>
      )}
    </div>
  );
}

// ── Credencial por-dispositivo del agente (ver estado + revocar/reactivar) ────
function AgentCredential({ assetId }) {
  const [cred, setCred] = useState(undefined); // undefined=cargando, null=sin credencial, obj=existe
  const base = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(() => {
    fetch(`${base}/agent/credentials/by-asset/${assetId}`, { headers })
      .then(r => (r.ok ? r.json() : null)).then(setCred).catch(() => setCred(null));
  }, [assetId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const setRevoked = async (revoked) => {
    if (!cred) return;
    if (revoked && !window.confirm('¿Revocar la credencial de este equipo? Dejará de autenticar contra el server hasta re-enrolarse.')) return;
    await fetch(`${base}/agent/credentials/${cred.id}`, { method: 'PATCH', headers, body: JSON.stringify({ revoked }) });
    load();
  };

  if (cred === undefined || cred === null) return null;   // sin credencial → no ensucia la fila
  const active = !cred.revoked;
  return (
    <span className="detail-chip"
      title={`Credencial de dispositivo${cred.last_used ? ' · último uso ' + new Date(cred.last_used).toLocaleString('es-AR') : ''}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: active ? 'var(--green-dim)' : 'var(--red-dim)', color: active ? 'var(--green)' : 'var(--red)' }}>
      <Shield size={10} /> {active ? 'Activa' : 'Revocada'}
      <button onClick={() => setRevoked(active)} title={active ? 'Revocar credencial' : 'Reactivar credencial'}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 700, textDecoration: 'underline', padding: 0, fontSize: 'inherit' }}>
        {active ? 'Revocar' : 'Reactivar'}
      </button>
    </span>
  );
}

// Chip + reveal del admin local de respaldo (cdmadmin). SE AUTO-OCULTA: el endpoint
// /commands/local-admin devuelve [] si el tenant no tiene la feature local_admin_backup, y
// el componente no renderiza nada si este equipo no tiene credencial guardada. Por eso es
// seguro en la ficha compartida — solo aparece en equipos de tenants habilitados (CDM).
// La contraseña se pide recién al hacer click (queda registrado quién y cuándo).
// Sobre de seguridad (break-glass) del admin local de respaldo. SE AUTO-OCULTA (sin feature /
// sin credencial no renderiza nada). "Abrir el sobre" pide un motivo (auditado), revela la
// contraseña con una VENTANA con contador y programa la ROTACIÓN automática después. Sobres
// marcados críticos requieren aprobación firmada (E1b). Reemplaza el "ver contraseña" directo.
const linkBtn = (color) => ({ background: 'none', border: 'none', cursor: 'pointer', color, fontWeight: 700, textDecoration: 'underline', padding: 0, fontSize: 'inherit' });
function AdminLocalReveal({ assetId }) {
  const [cred, setCred] = useState(undefined);   // undefined=cargando, null=no aplica, obj=hay
  const [shown, setShown] = useState(null);      // { username, password, expires_at }
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);  // { opening_id } (crítico esperando firma)
  const [now, setNow] = useState(Date.now());
  const base = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = () => fetch(`${base}/commands/local-admin`, { headers })
    .then(r => (r.ok ? r.json() : []))
    .then(list => setCred((list || []).find(c => c.asset_id === assetId) || null))
    .catch(() => setCred(null));
  useEffect(() => { load(); }, [assetId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Contador mientras la contraseña está visible.
  useEffect(() => {
    if (!shown) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [shown]);

  const abrir = async () => {
    const reason = window.prompt('Motivo para abrir el sobre (queda auditado y se avisa a los admins):');
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await fetch(`${base}/commands/local-admin/${assetId}/open`, {
        method: 'POST', headers, body: JSON.stringify({ reason: reason.trim() }) });
      const d = await r.json();
      if (r.status === 202) setPending({ opening_id: d.opening_id });   // crítico → esperar firma
      else if (r.ok) { setShown(d); load(); }
      else alert(d.error || 'No se pudo abrir el sobre.');
    } catch { alert('Error de red al abrir el sobre.'); }
    setBusy(false);
  };

  // Sobre crítico esperando firma: poléa la apertura; al aprobarse (firma del teléfono) revela.
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(async () => {
      const hist = await fetch(`${base}/commands/local-admin/${assetId}/openings`, { headers })
        .then(r => (r.ok ? r.json() : [])).catch(() => []);
      const op = hist.find(o => o.id === pending.opening_id);
      if (!op) return;
      if (op.status === 'approved') {
        const r = await fetch(`${base}/commands/local-admin/${assetId}/reveal-approved`, { method: 'POST', headers });
        const d = await r.json().catch(() => ({}));
        if (r.ok) setShown(d); else alert(d.error || 'No se pudo revelar tras la aprobación.');
        setPending(null); load();
      } else if (op.status === 'expired' || op.status === 'rotated') {
        setPending(null);
      }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const toggleCritical = async () => {
    try {
      const r = await fetch(`${base}/commands/local-admin/${assetId}/criticality`, {
        method: 'PATCH', headers, body: JSON.stringify({ critical: !cred.critical }) });
      if (r.ok) load();
    } catch { /* noop */ }
  };

  if (cred === undefined || cred === null) return null;

  if (shown) {
    const exp = shown.expires_at ? new Date(shown.expires_at).getTime() : 0;
    const left = Math.max(0, Math.floor((exp - now) / 1000));
    const mm = String(Math.floor(left / 60)).padStart(2, '0'), ss = String(left % 60).padStart(2, '0');
    return (
      <span className="detail-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--amber-dim)', color: 'var(--amber)' }}>
        <Key size={10} /> {shown.username}:
        <code style={{ fontWeight: 700, letterSpacing: 0.5 }}>{shown.password}</code>
        <button onClick={() => navigator.clipboard?.writeText(shown.password)} title="Copiar" style={linkBtn('inherit')}>copiar</button>
        <span style={{ fontWeight: 600 }}>· expira {mm}:{ss} · se rota luego</span>
        <button onClick={() => setShown(null)} title="Ocultar" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}><X size={11} /></button>
      </span>
    );
  }

  return (
    <span className="detail-chip"
      title={`Sobre break-glass · ${cred.username} · definido ${new Date(cred.set_at).toLocaleString('es-AR')}${cred.revealed_at ? ` · última apertura ${new Date(cred.revealed_at).toLocaleString('es-AR')}` : ''}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Key size={10} /> Sobre: {cred.username}
      {cred.critical && <span style={{ color: 'var(--red)', fontWeight: 700 }}>crítico</span>}
      {cred.rotate_pending && <span style={{ color: 'var(--ink-3)' }}>· rotación pendiente</span>}
      <button onClick={abrir} disabled={busy || !!pending} title="Abrir el sobre: revela la contraseña con ventana y la rota después (auditado)" style={linkBtn('var(--blue)')}>
        {pending ? 'esperando firma…' : busy ? '…' : 'abrir sobre'}
      </button>
      <button onClick={toggleCritical} title="Un sobre crítico exige aprobación firmada para abrirse" style={linkBtn('var(--ink-3)')}>
        {cred.critical ? 'quitar crítico' : 'marcar crítico'}
      </button>
    </span>
  );
}

function UsbTab({ assetId }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL_USB}/usb/asset/${assetId}`, { headers })
      .then(r => r.json())
      .then(d => setDevices(Array.isArray(d) ? d : []))
      .catch(() => setDevices([]))
      .finally(() => setLoading(false));
  }, [assetId]); // eslint-disable-line

  const fmtD = (d) => d ? new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  if (devices.length === 0) return (
    <div style={{ textAlign: 'center', padding: 48, color: 'var(--ink-3)' }}>
      <HardDrive size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Sin dispositivos USB registrados</div>
      <div style={{ fontSize: 12 }}>El agente reportará los dispositivos en el próximo ciclo de escaneo</div>
    </div>
  );

  return (
    <div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Dispositivo</th>
              <th>Serial</th>
              <th style={{ width: 130 }}>Estado</th>
              <th style={{ width: 50, textAlign: 'center' }}>Conex.</th>
              <th style={{ width: 130 }}>Primera vez</th>
              <th style={{ width: 130 }}>Última vez</th>
            </tr>
          </thead>
          <tbody>
            {devices.map(dev => {
              const st = USB_STATUS[dev.status] || USB_STATUS.unknown;
              return (
                <tr key={dev.id}>
                  <td>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{dev.friendly_name || dev.device_id || '—'}</div>
                    {dev.vendor_id && (
                      <div style={{ fontSize: 10, color: 'var(--ink-3)', fontFamily: 'monospace' }}>
                        VID:{dev.vendor_id} · PID:{dev.product_id}
                      </div>
                    )}
                  </td>
                  <td>
                    <code style={{ fontSize: 11, background: 'var(--bg)', padding: '2px 7px', borderRadius: 4 }}>
                      {dev.serial || '—'}
                    </code>
                    {dev.drive_letter && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--ink-2)' }}>({dev.drive_letter})</span>
                    )}
                  </td>
                  <td>
                    <span style={{ color: st.color, fontSize: 11, fontWeight: 600 }}>
                      {st.icon} {st.label}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--ink-2)' }}>{dev.connection_count}</td>
                  <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{fmtD(dev.first_seen)}</td>
                  <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{fmtD(dev.last_seen)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Hardware Health Tab ───────────────────────────────────────────────────────
const HW_API = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const GRADE_CFG = {
  healthy:   { label: '🟢 Saludable',        bg: 'var(--green-dim)', color: 'var(--green)', border: '#86efac' },
  monitor:   { label: '🟡 Monitorear',        bg: 'var(--amber-dim)', color: 'var(--amber)', border: '#fde047' },
  attention: { label: '🟠 Requiere atención', bg: 'var(--amber-dim)', color: 'var(--amber)', border: '#fdba74' },
  critical:  { label: '🔴 Crítico',           bg: 'var(--red-dim)', color: 'var(--red)', border: 'var(--red-dim)' },
};

const SMART_ATTR_LABELS = {
  5: 'Reallocated Sectors', 10: 'Spin Retry Count', 187: 'Reported Uncorrect',
  194: 'Temperatura Disco (°C)', 197: 'Current Pending Sectors',
  198: 'Uncorrectable Sectors', 199: 'UltraDMA CRC Errors',
};

const ALERT_TYPE_LABEL = {
  temperature_cpu: '🌡️ Temperatura CPU', cpu_throttling: '⚡ CPU Throttling',
  ram_usage: '🧠 Uso de RAM', ram_hardware_error: '💥 Error HW (WHEA)',
  battery_wear: '🔋 Desgaste Batería', smart: '💾 S.M.A.R.T.',
};

function SmartStatusBadge({ status }) {
  const cfg = {
    ok:       { bg: 'var(--green-dim)', color: 'var(--green)', label: 'OK' },
    warning:  { bg: 'var(--amber-dim)', color: 'var(--amber)', label: 'Advertencia' },
    critical: { bg: 'var(--red-dim)', color: 'var(--red)', label: 'Crítico' },
  }[status] || { bg: 'var(--bg)', color: 'var(--ink-2)', label: status };
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

function Sparkline({ data, color = 'var(--accent)', height = 40 }) {
  if (!data || data.length < 2) return <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Sin datos</span>;
  const w = 120;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={height} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={pts.split(' ').at(-1).split(',')[0]} cy={pts.split(' ').at(-1).split(',')[1]} r={3} fill={color} />
    </svg>
  );
}

function HwHealthTab({ asset }) {
  const [data, setData]       = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      fetch(`${HW_API}/hardware/asset/${asset.id}`, { headers }).then(r => r.json()),
      fetch(`${HW_API}/hardware/asset/${asset.id}/history`, { headers }).then(r => r.json()),
    ]).then(([d, h]) => {
      setData(d);
      setHistory(Array.isArray(h) ? h : []);
    }).catch(() => setData({})).finally(() => setLoading(false));
  }, [asset.id]); // eslint-disable-line

  const resolveAlert = async (alertId) => {
    await fetch(`${HW_API}/hardware/alerts/${alertId}/resolve`, { method: 'PATCH', headers });
    setData(prev => prev ? { ...prev, alerts: prev.alerts.filter(a => a.id !== alertId) } : prev);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;
  if (!data || !data.snapshot) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
        <Activity size={40} style={{ opacity: 0.2, display: 'block', margin: '0 auto 12px' }} />
        <div style={{ fontSize: 14, fontWeight: 600 }}>Sin datos de salud del hardware</div>
        <div style={{ fontSize: 12, marginTop: 4 }}>El agente enviará los datos en el próximo ciclo de escaneo.</div>
      </div>
    );
  }

  const { snapshot: s, smart, alerts, degradation, last_boot_time } = data;
  const grade = GRADE_CFG[s.health_grade] || GRADE_CFG.monitor;
  const cpuTemps  = history.map(h => parseFloat(h.cpu_temp_c)).filter(v => !isNaN(v));
  const ramUsages = history.map(h => parseFloat(h.ram_usage_pct)).filter(v => !isNaN(v));

  // Uptime (desde last_boot_time) + semáforo de degradación derivado por el backend
  const uptimeMs  = last_boot_time ? (Date.now() - new Date(last_boot_time).getTime()) : null;
  const uptimeStr = uptimeMs != null ? `${Math.floor(uptimeMs / 86400000)}d ${Math.floor((uptimeMs % 86400000) / 3600000)}h` : null;
  const DEG_CFG = {
    critical: { bg: 'var(--red-dim)', border: 'var(--red-dim)', color: 'var(--red)', label: 'Degradación crítica' },
    warning:  { bg: 'var(--amber-dim)', border: 'var(--amber-dim)', color: 'var(--amber)', label: 'Degradación de performance' },
    ok:       { bg: 'var(--green-dim)', border: 'var(--green-dim)', color: 'var(--green)', label: 'Sin degradación' },
  };
  const deg = degradation ? (DEG_CFG[degradation.level] || DEG_CFG.ok) : null;

  // Group SMART by disk
  const diskMap = {};
  (smart || []).forEach(a => {
    const key = a.disk_serial || a.disk_model;
    if (!diskMap[key]) diskMap[key] = { model: a.disk_model, serial: a.disk_serial, type: a.disk_type, attrs: [] };
    diskMap[key].attrs.push(a);
  });

  return (
    <div style={{ padding: '24px 0' }}>

      {/* Semáforo de degradación de performance */}
      {deg && (
        <div style={{ background: deg.bg, border: `1px solid ${deg.border}`, borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: 13, color: deg.color }}>
            <Activity size={16} /> {deg.label}
            {uptimeStr && <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>Uptime: {uptimeStr}</span>}
          </div>
          {degradation.reasons?.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 12, color: 'var(--ink-2)' }}>
              {degradation.reasons.map((r, i) => <li key={i}>{r.text}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Score + Alertas activas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 20, marginBottom: 24, alignItems: 'start' }}>
        {/* Score */}
        <div style={{ background: grade.bg, border: `2px solid ${grade.border}`, borderRadius: 16, padding: '20px 28px', textAlign: 'center', minWidth: 140 }}>
          <div style={{ fontSize: 42, fontWeight: 900, color: grade.color, lineHeight: 1 }}>{s.health_score ?? '—'}</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: grade.color, marginTop: 4, textTransform: 'uppercase', letterSpacing: 1 }}>/ 100</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: grade.color, marginTop: 8 }}>{grade.label}</div>
        </div>

        {/* Alertas activas */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--bg)', fontWeight: 700, fontSize: 13 }}>
            🚨 Alertas activas ({alerts?.length || 0})
          </div>
          {!alerts?.length ? (
            <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
              ✅ Sin alertas activas — hardware en buen estado
            </div>
          ) : (
            <div style={{ maxHeight: 180, overflowY: 'auto' }}>
              {alerts.map(al => (
                <div key={al.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--bg-subtle)' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, flexShrink: 0,
                    ...(al.severity === 'critical' ? { background: 'var(--red-dim)', color: 'var(--red)' } :
                        al.severity === 'warning'  ? { background: 'var(--amber-dim)', color: 'var(--amber)' } :
                                                     { background: 'var(--accent-dim)', color: 'var(--accent)' })
                  }}>
                    {al.severity === 'critical' ? 'Crítico' : al.severity === 'warning' ? 'Advertencia' : 'Info'}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)' }}>{ALERT_TYPE_LABEL[al.alert_type] || al.alert_type}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-2)' }}>{al.message}</div>
                  </div>
                  <button onClick={() => resolveAlert(al.id)}
                    title="Marcar como resuelta"
                    style={{ background: 'none', border: '1px solid var(--border-2)', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: 'var(--ink-2)', flexShrink: 0 }}>
                    ✓ Resolver
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Métricas instantáneas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14, marginBottom: 24 }}>
        <MetricCard icon={<ThermometerSun size={18} />} label="Temp. CPU" value={s.cpu_temp_c != null ? `${s.cpu_temp_c}°C` : '—'}
          sub={s.cpu_throttling ? '⚡ Throttling detectado' : null}
          color={s.cpu_temp_c > 90 ? 'var(--red)' : s.cpu_temp_c > 75 ? 'var(--amber)' : 'var(--green)'}
          chartData={cpuTemps} chartColor={s.cpu_temp_c > 75 ? '#f97316' : 'var(--accent)'} />
        <MetricCard icon={<Activity size={18} />} label="Uso CPU" value={s.cpu_usage_pct != null ? `${s.cpu_usage_pct}%` : '—'}
          color={s.cpu_usage_pct > 90 ? 'var(--red)' : 'var(--ink-2)'} />
        <MetricCard icon={<Database size={18} />} label="Uso RAM" value={s.ram_usage_pct != null ? `${s.ram_usage_pct}%` : '—'}
          sub={s.ram_available_mb != null ? `${(s.ram_available_mb/1024).toFixed(1)} GB libres` : null}
          color={s.ram_usage_pct > 95 ? 'var(--red)' : s.ram_usage_pct > 85 ? 'var(--amber)' : 'var(--green)'}
          chartData={ramUsages} chartColor={s.ram_usage_pct > 85 ? '#f97316' : 'var(--accent)'} />
        {uptimeStr && (
          <MetricCard icon={<Activity size={18} />} label="Uptime" value={uptimeStr}
            sub={uptimeMs > 14*86400000 ? '⚠ Conviene reiniciar' : null}
            color={uptimeMs > 14*86400000 ? 'var(--red)' : uptimeMs > 7*86400000 ? 'var(--amber)' : 'var(--green)'} />
        )}
        {s.mem_compression_mb != null && (
          <MetricCard icon={<Database size={18} />} label="Memory Compression" value={`${(s.mem_compression_mb/1024).toFixed(1)} GB`}
            sub={s.mem_compression_mb >= 1024 ? 'Presión de RAM' : null}
            color={s.mem_compression_mb >= 1024 ? 'var(--amber)' : 'var(--green)'} />
        )}
        {s.pagefile_usage_pct != null && (
          <MetricCard icon={<Database size={18} />} label="Uso pagefile" value={`${s.pagefile_usage_pct}%`}
            color={s.pagefile_usage_pct >= 80 ? 'var(--amber)' : 'var(--ink-2)'} />
        )}
        {s.disk_active_pct != null && (
          <MetricCard icon={<HardDrive size={18} />} label="Disco activo" value={`${s.disk_active_pct}%`}
            color={s.disk_active_pct >= 90 ? 'var(--red)' : 'var(--ink-2)'} />
        )}
        {s.disk_temp_c != null && (
          <MetricCard icon={<ThermometerSun size={18} />} label="Temp. Disco" value={`${s.disk_temp_c}°C`}
            color={s.disk_temp_c > 65 ? 'var(--red)' : s.disk_temp_c > 55 ? 'var(--amber)' : 'var(--green)'} />
        )}
        {s.battery_wear_pct != null && s.battery_wear_pct > 0 && (
          <MetricCard icon={<BatteryLow size={18} />} label="Desgaste batería" value={`${s.battery_wear_pct}%`}
            sub={`${s.battery_full_mwh || '—'} / ${s.battery_design_mwh || '—'} mWh`}
            color={s.battery_wear_pct > 70 ? 'var(--red)' : s.battery_wear_pct > 50 ? 'var(--amber)' : 'var(--green)'} />
        )}
        {s.whea_errors > 0 && (
          <MetricCard icon={<AlertTriangle size={18} />} label="Errores WHEA" value={s.whea_errors}
            sub="Errores hardware RAM (24h)" color="var(--red)" />
        )}
      </div>

      {/* Top procesos por RAM */}
      {Array.isArray(s.top_processes) && s.top_processes.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--bg)', fontWeight: 700, fontSize: 13 }}>
            🧠 Procesos que más RAM consumen
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {s.top_processes.map((p, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--bg-subtle)' }}>
                  <td style={{ padding: '8px 16px', fontSize: 12, color: 'var(--ink-2)' }}>
                    {p.name}{p.count > 1 ? <span style={{ color: 'var(--ink-3)' }}> ({p.count} procesos)</span> : null}
                  </td>
                  <td style={{ padding: '8px 16px', fontSize: 12, textAlign: 'right', fontWeight: 600, color: 'var(--ink-2)' }}>
                    {(p.ram_mb / 1024).toFixed(2)} GB
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* SMART por disco */}
      {Object.keys(diskMap).length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 0 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--bg)', fontWeight: 700, fontSize: 13 }}>
            💾 Atributos S.M.A.R.T.
          </div>
          {Object.entries(diskMap).map(([key, disk]) => (
            <div key={key}>
              <div style={{ padding: '10px 20px', background: 'var(--bg-subtle)', borderBottom: '1px solid var(--bg)', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <HardDrive size={14} style={{ color: 'var(--ink-3)' }} />
                {disk.model}
                {disk.serial && <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>· S/N: {disk.serial}</span>}
                <span style={{ color: '#0369a1', fontSize: 10, fontWeight: 700 }}>{disk.type}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-subtle)' }}>
                    <th style={{ padding: '8px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', textTransform: 'uppercase' }}>Atributo</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', textTransform: 'uppercase', width: 100 }}>Valor</th>
                    <th style={{ padding: '8px 20px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', textTransform: 'uppercase', width: 120 }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {disk.attrs.map((a, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--bg)' }}>
                      <td style={{ padding: '10px 20px', fontSize: 13, color: 'var(--ink-2)' }}>
                        <span style={{ fontSize: 11, color: 'var(--ink-3)', marginRight: 6 }}>#{a.attribute_id}</span>
                        {SMART_ATTR_LABELS[a.attribute_id] || a.attribute_name}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 700, textAlign: 'right', color: a.status === 'critical' ? 'var(--red)' : a.status === 'warning' ? 'var(--amber)' : 'var(--ink)', fontFamily: 'monospace' }}>
                        {a.raw_value}
                      </td>
                      <td style={{ padding: '10px 20px', textAlign: 'center' }}>
                        <SmartStatusBadge status={a.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, sub, color = 'var(--ink-2)', chartData, chartColor }) {
  return (
    <div className="card" style={{ padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ink-3)', marginBottom: 8 }}>
        {icon}
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>{sub}</div>}
      {chartData && chartData.length > 1 && (
        <div style={{ marginTop: 10 }}>
          <Sparkline data={chartData} color={chartColor || 'var(--accent)'} />
        </div>
      )}
    </div>
  );
}

// ── Control de bloqueo USB (política declarativa) ─────────────────────────────
function UsbBlockControl({ asset }) {
  const [blocked, setBlocked] = useState(asset.usb_storage_blocked || false);
  const [saving, setSaving]   = useState(false);
  const token   = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const toggle = async () => {
    const next = !blocked;
    const msg  = next
      ? `¿Bloquear USB storage en ${asset.hostname}?\n\nLos pendrives y discos externos no podrán montarse. El mouse, teclado y demás periféricos siguen funcionando normalmente.\n\nEl agente aplicará el cambio en el próximo ciclo de escaneo.`
      : `¿Habilitar USB storage en ${asset.hostname}?\n\nLos pendrives y discos externos podrán volver a conectarse.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL_USB}/usb/policy/${asset.id}`, {
        method: 'PATCH', headers, body: JSON.stringify({ blocked: next }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setBlocked(d.usb_storage_blocked);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '3px 12px', borderRadius: 20,
        background: blocked ? 'var(--red-dim)' : 'var(--green-dim)',
        color:      blocked ? 'var(--red)' : 'var(--green)',
        border:     `1px solid ${blocked ? 'var(--red-dim)' : 'var(--green-dim)'}`,
      }}>
        {blocked ? '🚫 USB bloqueado' : '✅ USB activo'}
      </span>
      <button onClick={toggle} disabled={saving} style={{
        padding: '7px 16px', border: 'none', borderRadius: 8,
        fontSize: 12, fontWeight: 700, cursor: 'pointer',
        background: blocked ? 'var(--green)' : 'var(--red)', color: 'var(--surface)',
        opacity: saving ? 0.6 : 1,
      }}>
        {saving ? 'Guardando...' : blocked ? '✅ Habilitar USB' : '🚫 Bloquear USB'}
      </button>
    </div>
  );
}

function MarketPricePanel({ asset }) {
  const [state, setState] = useState('idle'); // idle | loading | found | notfound | error
  const [modelName, setModelName] = useState(null);
  const [manualModel, setManualModel] = useState('');
  const [showManual, setShowManual] = useState(false);

  const isLenovo = /lenovo/i.test(asset?.manufacturer || '');
  const productCode = asset?.product_number || asset?.serial_number || '';
  if (!productCode) return null;

  const doLookup = async (customName) => {
    const searchName = customName || null;
    setState('loading');
    try {
      let name = searchName;
      if (!name && isLenovo) {
        name = await lookupLenovoName(productCode);
      }
      if (name) {
        setModelName(name);
        setState('found');
      } else {
        setState('notfound');
        setShowManual(true);
      }
    } catch {
      setState('error');
    }
  };

  const searchQuery = encodeURIComponent((modelName || manualModel) + ' used laptop');
  const ebayUrl    = `https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(modelName || manualModel)}&LH_Sold=1&LH_Complete=1`;
  const mlaUrl     = `https://listado.mercadolibre.com.ar/${encodeURIComponent((modelName || manualModel).replace(/ /g,'-'))}-usado`;

  return (
    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '14px 16px', marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <TrendingUp size={16} style={{ color: 'var(--accent)' }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>Valor de mercado</span>
      </div>

      {state === 'idle' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
            Código: <code style={{ background: '#e0f2fe', padding: '1px 6px', borderRadius: 4 }}>{productCode}</code>
          </span>
          <button
            onClick={() => doLookup(null)}
            style={{ background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <Search size={13} /> Buscar nombre comercial
          </button>
        </div>
      )}

      {state === 'loading' && (
        <p style={{ fontSize: 12, color: 'var(--ink-2)', margin: 0 }}>🔍 Consultando base de datos de Lenovo...</p>
      )}

      {state === 'found' && (
        <div>
          <p style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 8 }}>
            ✅ Nombre comercial: <strong>{modelName}</strong>
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <a href={ebayUrl} target="_blank" rel="noreferrer"
              style={{ color: 'var(--surface)', fontSize: 12, fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
              <ExternalLink size={12} /> eBay (vendidos)
            </a>
            <a href={mlaUrl} target="_blank" rel="noreferrer"
              style={{ color: '#333', fontSize: 12, fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
              <ExternalLink size={12} /> MercadoLibre
            </a>
            <button onClick={() => { setState('idle'); setModelName(null); setShowManual(false); }}
              style={{ background: 'transparent', border: '1px solid var(--border-2)', borderRadius: 6, padding: '5px 10px', fontSize: 12, color: 'var(--ink-2)', cursor: 'pointer' }}>
              Reintentar
            </button>
          </div>
        </div>
      )}

      {(state === 'notfound' || state === 'error') && (
        <div>
          <p style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 8 }}>
            {state === 'error' ? '⚠️ Error al consultar.' : '⚠️ No se encontró el nombre comercial automáticamente.'}
          </p>
        </div>
      )}

      {showManual && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            placeholder="Ingresá el nombre comercial (ej: ThinkPad T14 Gen 2)"
            value={manualModel}
            onChange={e => setManualModel(e.target.value)}
            style={{ flex: 1, padding: '5px 10px', fontSize: 12, border: '1px solid var(--border-2)', borderRadius: 6 }}
          />
          <button
            onClick={() => { setModelName(manualModel); setState('found'); setShowManual(false); }}
            disabled={!manualModel.trim()}
            style={{ background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            Buscar
          </button>
        </div>
      )}
    </div>
  );
}

function CostForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {
    category: 'license', description: '', vendor: '', amount: '',
    currency: 'USD', frequency: 'monthly', due_date: '', notes: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Categoría *</label>
          <select className="form-input" value={form.category} onChange={e => set('category', e.target.value)}>
            {COST_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Descripción *</label>
          <input className="form-input" placeholder="ej: SQL Server Enterprise" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Proveedor</label>
          <input className="form-input" placeholder="ej: Microsoft" value={form.vendor} onChange={e => set('vendor', e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 1fr', gap: 6 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Monto *</label>
            <input className="form-input" type="number" min="0" step="0.01" placeholder="0.00" value={form.amount} onChange={e => set('amount', e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Moneda</label>
            <select className="form-input" value={form.currency} onChange={e => set('currency', e.target.value)}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Frecuencia</label>
            <select className="form-input" value={form.frequency} onChange={e => set('frequency', e.target.value)}>
              <option value="monthly">Mensual</option>
              <option value="annual">Anual</option>
              <option value="one_time">Único</option>
            </select>
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Fecha de vencimiento</label>
          <input className="form-input" type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Notas</label>
          <input className="form-input" placeholder="Observaciones..." value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancelar</button>
        <button className="btn btn-primary btn-sm" onClick={() => onSave(form)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Check size={13} /> Guardar
        </button>
      </div>
    </div>
  );
}

// ── Compra y Depreciación ─────────────────────────────────────────────────────
function PurchaseSection({ assetId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({
    purchase_date: '', purchase_price: '', currency: 'USD', vendor: '',
    invoice_number: '', warranty_until: '', useful_life_years: 5,
    residual_value: 0, depreciation_method: 'linear', notes: '',
  });
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    fetch(`${API_URL}/costs/purchase/${assetId}`, { headers })
      .then(r => r.json()).then(d => {
        setData(d);
        if (d) setForm({
          purchase_date:       d.purchase_date?.split('T')[0] || '',
          purchase_price:      d.purchase_price || '',
          currency:            d.currency || 'USD',
          vendor:              d.vendor || '',
          invoice_number:      d.invoice_number || '',
          warranty_until:      d.warranty_until?.split('T')[0] || '',
          useful_life_years:   d.useful_life_years || 5,
          residual_value:      d.residual_value || 0,
          depreciation_method: d.depreciation_method || 'linear',
          notes:               d.notes || '',
        });
      }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [assetId]);

  const handleSave = async () => {
    const r = await fetch(`${API_URL}/costs/purchase/${assetId}`, {
      method: 'POST', headers, body: JSON.stringify(form),
    });
    if (r.ok) { setEditing(false); load(); }
    else { const d = await r.json(); alert(d.error || 'Error'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Eliminar datos de compra y depreciacion?')) return;
    await fetch(`${API_URL}/costs/purchase/${assetId}`, { method: 'DELETE', headers });
    setData(null); setEditing(false);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const today = new Date();
  const warrantyExpired = data?.warranty_until && new Date(data.warranty_until) < today;
  const warrantyExpiringSoon = data?.warranty_until && !warrantyExpired &&
    new Date(data.warranty_until) <= new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);

  if (loading) return <div style={{ padding: 24, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink)' }}>Compra y depreciacion</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {data && !editing && (
            <button onClick={handleDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 12 }}>
              <Trash2 size={13} />
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(!editing)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {editing ? <><X size={12}/> Cancelar</> : data ? <><Pencil size={12}/> Editar</> : <><Plus size={12}/> Registrar compra</>}
          </button>
        </div>
      </div>

      {editing ? (
        <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Fecha de compra</label>
              <input className="form-input" type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Precio de compra *</label>
              <input className="form-input" type="number" min="0" step="0.01" placeholder="0.00" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Moneda</label>
              <select className="form-input" value={form.currency} onChange={e => set('currency', e.target.value)}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Proveedor</label>
              <input className="form-input" placeholder="ej: Dell Argentina" value={form.vendor} onChange={e => set('vendor', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Nro. factura</label>
              <input className="form-input" placeholder="FAC-0001234" value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Garantia hasta</label>
              <input className="form-input" type="date" value={form.warranty_until} onChange={e => set('warranty_until', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Vida util (anos)</label>
              <select className="form-input" value={form.useful_life_years} onChange={e => set('useful_life_years', parseInt(e.target.value))}>
                {[1,2,3,4,5,6,7,8,10,12,15,20].map(y => <option key={y} value={y}>{y} anos</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Valor residual</label>
              <input className="form-input" type="number" min="0" step="0.01" placeholder="0.00" value={form.residual_value} onChange={e => set('residual_value', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Metodo</label>
              <select className="form-input" value={form.depreciation_method} onChange={e => set('depreciation_method', e.target.value)}>
                <option value="linear">Lineal</option>
                <option value="none">Sin depreciacion</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>Notas</label>
            <input className="form-input" placeholder="Observaciones..." value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>Cancelar</button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Check size={13} /> Guardar
            </button>
          </div>
        </div>
      ) : data ? (
        <div>
          {/* Alertas */}
          {warrantyExpired && (
            <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--red)', fontWeight: 600 }}>
              Garantia vencida el {new Date(data.warranty_until).toLocaleDateString('es-AR')}
            </div>
          )}
          {warrantyExpiringSoon && (
            <div style={{ background: 'var(--amber-dim)', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: 12, color: 'var(--amber)' }}>
              Garantia vence el {new Date(data.warranty_until).toLocaleDateString('es-AR')}
            </div>
          )}

          {/* Tarjetas de depreciacion */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 14 }}>
            <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>PRECIO COMPRA</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--ink)' }}>{fmtMoney(data.purchase_price, data.currency)}</div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>{data.purchase_date ? new Date(data.purchase_date).toLocaleDateString('es-AR') : 'Sin fecha'}</div>
            </div>
            <div style={{ background: 'var(--accent-dim)', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--accent)', marginBottom: 4 }}>VALOR ACTUAL</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#1e40af' }}>{fmtMoney(data.current_value, data.currency)}</div>
              <div style={{ fontSize: 10, color: 'var(--accent-dim)' }}>hoy</div>
            </div>
            <div style={{ background: data.fully_depreciated ? 'var(--red-dim)' : 'var(--green-dim)', border: `1px solid ${data.fully_depreciated ? 'var(--red-dim)' : 'var(--green-dim)'}`, borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: data.fully_depreciated ? 'var(--red)' : 'var(--green)', marginBottom: 4 }}>DEPRECIADO</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: data.fully_depreciated ? 'var(--red)' : 'var(--green)' }}>{data.pct_depreciated}%</div>
              <div style={{ fontSize: 10, color: data.fully_depreciated ? 'var(--red-dim)' : '#86efac' }}>
                {data.fully_depreciated ? 'totalmente depreciado' : `${data.months_remaining} meses restantes`}
              </div>
            </div>
            <div style={{ background: 'var(--amber-dim)', border: '1px solid var(--amber-dim)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--amber)', marginBottom: 4 }}>DEP. MENSUAL</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--amber)' }}>{fmtMoney(data.monthly_dep, data.currency)}</div>
              <div style={{ fontSize: 10, color: '#fdba74' }}>{fmtMoney(data.annual_dep, data.currency)}/ano</div>
            </div>
          </div>

          {/* Barra de depreciacion */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ink-2)', marginBottom: 4 }}>
              <span>Vida util: {data.useful_life_years} anos ({data.months_elapsed} meses transcurridos)</span>
              <span>{data.pct_depreciated}% depreciado</span>
            </div>
            <div style={{ height: 8, background: 'var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(100, data.pct_depreciated)}%`, background: data.pct_depreciated >= 100 ? 'var(--red)' : data.pct_depreciated >= 80 ? 'var(--amber)' : 'var(--accent)', borderRadius: 8, transition: 'width 0.5s' }} />
            </div>
          </div>

          {/* Datos de compra */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 12 }}>
            {data.vendor && <div><span style={{ color: 'var(--ink-3)' }}>Proveedor: </span><span style={{ fontWeight: 500 }}>{data.vendor}</span></div>}
            {data.invoice_number && <div><span style={{ color: 'var(--ink-3)' }}>Factura: </span><span style={{ fontWeight: 500 }}>{data.invoice_number}</span></div>}
            {data.warranty_until && (
              <div>
                <span style={{ color: 'var(--ink-3)' }}>Garantia: </span>
                <span style={{ fontWeight: 500, color: warrantyExpired ? 'var(--red)' : 'var(--ink-2)' }}>
                  {warrantyExpired ? 'Vencida ' : ''}{new Date(data.warranty_until).toLocaleDateString('es-AR')}
                </span>
              </div>
            )}
            <div><span style={{ color: 'var(--ink-3)' }}>Metodo: </span><span style={{ fontWeight: 500 }}>{data.depreciation_method === 'linear' ? 'Lineal' : 'Sin depreciacion'}</span></div>
            <div><span style={{ color: 'var(--ink-3)' }}>Valor residual: </span><span style={{ fontWeight: 500 }}>{fmtMoney(data.residual_value, data.currency)}</span></div>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--ink-3)', fontSize: 13, background: 'var(--bg-subtle)', borderRadius: 10, border: '1px dashed var(--border)' }}>
          Sin datos de compra. Haga clic en "Registrar compra" para agregar.
        </div>
      )}



      <div style={{ height: 1, background: 'var(--border)', margin: '20px 0' }} />
    </div>
  );
}

function CostsTab({ assetId }) {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    fetch(`${API_URL}/costs/asset/${assetId}`, { headers })
      .then(r => r.json()).then(d => setCosts(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [assetId]);

  const handleAdd = async (form) => {
    const r = await fetch(`${API_URL}/costs/asset/${assetId}`, {
      method: 'POST', headers, body: JSON.stringify(form),
    });
    if (r.ok) { setShowAdd(false); load(); }
    else { const d = await r.json(); alert(d.error || 'Error'); }
  };

  const handleEdit = async (id, form) => {
    const r = await fetch(`${API_URL}/costs/${id}`, {
      method: 'PUT', headers, body: JSON.stringify(form),
    });
    if (r.ok) { setEditId(null); load(); }
    else { const d = await r.json(); alert(d.error || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta línea de costo?')) return;
    await fetch(`${API_URL}/costs/${id}`, { method: 'DELETE', headers });
    load();
  };

  // Totales
  const monthlyTotal = costs.filter(c => c.frequency !== 'one_time')
    .reduce((s, c) => s + parseFloat(c.monthly_equivalent || 0), 0);
  const annualTotal = monthlyTotal * 12;
  const oneTimeTotal = costs.filter(c => c.frequency === 'one_time')
    .reduce((s, c) => s + parseFloat(c.amount || 0), 0);

  // Próximos vencimientos
  const today = new Date();
  const in60 = new Date(today); in60.setDate(today.getDate() + 60);
  const expiring = costs.filter(c => c.due_date && new Date(c.due_date) <= in60 && new Date(c.due_date) >= today);

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div>
      {/* Compra y Depreciacion */}
      <PurchaseSection assetId={assetId} />

      {/* Resumen costos operativos */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <div style={{ background: 'var(--accent-dim)', border: '1px solid #bfdbfe', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, marginBottom: 4 }}>COSTO MENSUAL</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1e40af' }}>{fmtMoney(monthlyTotal, costs[0]?.currency || 'USD')}</div>
          <div style={{ fontSize: 11, color: 'var(--accent-dim)' }}>recurrente</div>
        </div>
        <div style={{ background: 'var(--green-dim)', border: '1px solid var(--green-dim)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600, marginBottom: 4 }}>COSTO ANUAL</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--green)' }}>{fmtMoney(annualTotal, costs[0]?.currency || 'USD')}</div>
          <div style={{ fontSize: 11, color: '#86efac' }}>proyectado</div>
        </div>
        <div style={{ background: 'var(--amber-dim)', border: '1px solid var(--amber-dim)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--amber)', fontWeight: 600, marginBottom: 4 }}>INVERSIÓN ÚNICA</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--amber)' }}>{fmtMoney(oneTimeTotal, costs[0]?.currency || 'USD')}</div>
          <div style={{ fontSize: 11, color: '#fdba74' }}>no recurrente</div>
        </div>
      </div>

      {/* Alerta de vencimientos */}
      {expiring.length > 0 && (
        <div style={{ background: 'var(--amber-dim)', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--amber)' }}>
          ⚠️ <strong>{expiring.length} vencimiento{expiring.length > 1 ? 's' : ''}</strong> en los próximos 60 días:{' '}
          {expiring.map(c => `${c.description} (${new Date(c.due_date).toLocaleDateString('es-AR')})`).join(', ')}
        </div>
      )}

      {/* Botón agregar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>{costs.length} línea{costs.length !== 1 ? 's' : ''} de costo</div>
        <button className="btn btn-primary btn-sm" onClick={() => { setShowAdd(true); setEditId(null); }}
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Plus size={13} /> Agregar costo
        </button>
      </div>

      {/* Formulario agregar */}
      {showAdd && <CostForm onSave={handleAdd} onCancel={() => setShowAdd(false)} />}

      {/* Tabla de costos */}
      {costs.length === 0 && !showAdd ? (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--ink-3)', fontSize: 13 }}>
          Sin costos registrados. Hacé clic en "Agregar costo" para comenzar.
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)' }}>Categoría</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)' }}>Descripción</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)' }}>Proveedor</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--ink-2)' }}>Monto</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)' }}>Frecuencia</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--ink-2)' }}>Mensual equiv.</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)' }}>Vence</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: 'var(--ink-2)' }}></th>
              </tr>
            </thead>
            <tbody>
              {costs.map(c => {
                const cat = COST_CATEGORIES.find(x => x.value === c.category) || COST_CATEGORIES[6];
                const isExpiringSoon = c.due_date && new Date(c.due_date) <= in60 && new Date(c.due_date) >= today;
                const isExpired = c.due_date && new Date(c.due_date) < today;
                return editId === c.id ? (
                  <tr key={c.id}><td colSpan={8} style={{ padding: '8px 12px' }}>
                    <CostForm initial={{ ...c, due_date: c.due_date ? c.due_date.split('T')[0] : '' }}
                      onSave={form => handleEdit(c.id, form)}
                      onCancel={() => setEditId(null)} />
                  </td></tr>
                ) : (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--bg)' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ color: cat.color, fontSize: 11, fontWeight: 600 }}>{cat.label}</span>
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 500 }}>{c.description}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--ink-2)' }}>{c.vendor || '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{fmtMoney(c.amount, c.currency)}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--ink-2)' }}>{FREQ_LABELS[c.frequency]?.label}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--accent)', fontWeight: 600 }}>
                      {c.frequency === 'one_time' ? '—' : fmtMoney(c.monthly_equivalent, c.currency)}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {c.due_date ? (
                        <span style={{ color: isExpired ? 'var(--red)' : isExpiringSoon ? 'var(--amber)' : 'var(--ink-2)', fontWeight: isExpiringSoon || isExpired ? 600 : 400 }}>
                          {isExpired ? '⚠ ' : isExpiringSoon ? '⏰ ' : ''}{new Date(c.due_date).toLocaleDateString('es-AR')}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button onClick={() => { setEditId(c.id); setShowAdd(false); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', padding: 4 }} title="Editar">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => handleDelete(c.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', padding: 4 }} title="Eliminar">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

// ── Actions Tab (Windows Update remoto) ──────────────────────────────────────

const CLOUD_LOCATION_NAMES = [
  'Amazon Web Services', 'Microsoft Azure', 'Google Cloud Platform',
  'VMware vSphere', 'Hyper-V', 'KVM / Proxmox', 'VirtualBox',
  'Xen', 'Contenedores / Docker', 'DigitalOcean',
];

const LIFECYCLE_OPTIONS = [
  { value: 'active',          label: 'Active',          color: 'var(--green)', bg: 'var(--green-dim)' },
  { value: 'decommissioned',  label: 'Decommissioned',  color: 'var(--ink-2)', bg: 'var(--bg)' },
  { value: 'deleted',         label: 'Deleted',         color: 'var(--red)', bg: 'var(--red-dim)' },
  { value: 'in_repair',       label: 'In Repair',       color: '#D45D00', bg: 'var(--amber-dim)' },
  { value: 'in_stock',        label: 'In Stock',        color: 'var(--accent)', bg: '#E6F1FB' },
  { value: 'merged',          label: 'Merged',          color: 'var(--ink-2)', bg: 'var(--bg-subtle)' },
  { value: 'stolen',          label: 'Stolen',          color: '#9B1C1C', bg: 'var(--red-dim)' },
];

const GRADE_MAP = {
  secure:     { label: 'Seguro',            color: 'var(--green)', bg: 'var(--green-dim)' },
  acceptable: { label: 'Aceptable',         color: 'var(--accent)', bg: '#E6F1FB' },
  attention:  { label: 'Atención requerida',color: '#D45D00', bg: 'var(--amber-dim)' },
  critical:   { label: 'Crítico',           color: 'var(--red)', bg: 'var(--red-dim)' },
};

const Field = ({ label, value }) => (
  <div className="field">
    <div className="field-label">{label}</div>
    <div className="field-value">{value || '\u2014'}</div>
  </div>
);

const statusBadge = (s) => {
  const map = { active: ['badge-active', 'Al día'], warning: ['badge-warning', 'Parche pendiente'], critical: ['badge-critical', 'Crítico'], offline: ['badge-offline', 'Offline'] };
  const [cls, label] = map[s] || ['badge-offline', s];
  return <span className={`badge ${cls}`}>{label}</span>;
};

const changeTypeLabel = (t) => {
  const map = {
    initial_registration: { label: 'Registro inicial', cls: 'badge-active' },
    update: { label: 'Actualización', cls: 'badge-offline' },
    user_change: { label: 'Cambio de usuario', cls: 'badge-warning' },
    admin_note: { label: 'Nota manual', cls: '' },
  };
  const { label, cls } = map[t] || { label: t, cls: '' };
  return <span className={`badge ${cls}`} style={!cls ? { background: '#e8f1f8', color: 'var(--accent)' } : {}}>{label}</span>;
};


// -- CommandsTab --------------------------------------------------------------
function CommandsTab({ asset, user }) {
  const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
  const token = localStorage.getItem('token');
  const headers = { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' };

  const [commands, setCommands] = React.useState([]);
  const [types, setTypes] = React.useState([]);
  const [selectedType, setSelectedType] = React.useState('');
  const [shellCmd, setShellCmd] = React.useState('');
  const [serviceName, setServiceName] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [pollingId, setPollingId] = React.useState(null);
  const [expandedId, setExpandedId] = React.useState(null);

  const canSend = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'technician';

  React.useEffect(() => {
    loadHistory();
    fetch(`${API_URL}/commands/types`, { headers })
      .then(r => r.json()).then(setTypes).catch(() => {});
  }, [asset.id]);

  React.useEffect(() => {
    if (!pollingId) return;
    const iv = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/commands/${pollingId}`, { headers });
        const cmd = await r.json();
        if (['completed','failed','timeout','cancelled'].includes(cmd.status)) {
          setPollingId(null);
          loadHistory();
          setExpandedId(pollingId);
        }
      } catch {}
    }, 2000);
    return () => clearInterval(iv);
  }, [pollingId]);

  async function loadHistory() {
    try {
      const r = await fetch(`${API_URL}/commands?asset_id=${asset.id}&limit=20`, { headers });
      const data = await r.json();
      if (Array.isArray(data)) setCommands(data);
    } catch {}
  }

  async function sendCommand() {
    if (!selectedType) return;
    setSending(true);
    try {
      const payload = {};
      if (selectedType === 'shell') payload.cmd = shellCmd;
      if (selectedType === 'restart_service') payload.service = serviceName;
      if (selectedType === 'get_logs') payload.lines = 50;

      const r = await fetch(`${API_URL}/commands`, {
        method: 'POST', headers,
        body: JSON.stringify({ asset_id: asset.id, command_type: selectedType, payload, timeout_sec: 60 })
      });
      const cmd = await r.json();
      if (cmd.id) {
        setPollingId(cmd.id);
        setShellCmd('');
        loadHistory();
      } else {
        alert(cmd.error || 'Error enviando comando');
      }
    } catch { alert('Error de red'); }
    finally { setSending(false); }
  }

  async function cancelCommand(id) {
    try {
      await fetch(`${API_URL}/commands/${id}`, { method: 'DELETE', headers });
      loadHistory();
      if (pollingId === id) setPollingId(null);
    } catch {}
  }

  const STATUS_STYLES = {
    pending:   { bg: 'var(--amber-dim)', color: 'var(--amber)', label: 'Pendiente',  dot: 'var(--amber)' },
    running:   { bg: '#e0f2fe', color: '#0369a1', label: 'Ejecutando', dot: '#0ea5e9' },
    completed: { bg: 'var(--green-dim)', color: 'var(--green)', label: 'Completado', dot: 'var(--green)' },
    failed:    { bg: 'var(--red-dim)', color: 'var(--red)', label: 'Fallido',    dot: 'var(--red)' },
    timeout:   { bg: 'var(--amber-dim)', color: 'var(--amber)', label: 'Timeout',    dot: 'var(--amber)' },
    cancelled: { bg: 'var(--bg)', color: 'var(--ink-2)', label: 'Cancelado',  dot: 'var(--ink-3)' },
  };
  const TYPE_LABELS = {
    ping: 'Ping', update_scan: 'Actualizar inventario', get_processes: 'Procesos activos',
    get_services: 'Servicios', get_disk_usage: 'Uso de disco', get_logs: 'Logs del sistema',
    restart_service: 'Reiniciar servicio', shell: 'Comando personalizado',
  };

  return (
    <div>
      <div className="detail-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Terminal size={16} /> Command & Control
        {pollingId && (
          <span style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 10, marginLeft: 8, animation: 'pulse 1.5s infinite' }}>
            Esperando respuesta del agente...
          </span>
        )}
      </div>

      {canSend && (
        <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>TIPO DE COMANDO</label>
              <select value={selectedType} onChange={e => setSelectedType(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13, background: 'var(--surface)' }}>
                <option value="">Seleccionar...</option>
                {types.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
              </select>
            </div>
            {selectedType === 'shell' && (
              <div style={{ flex: 2, minWidth: 200 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>COMANDO</label>
                <input value={shellCmd} onChange={e => setShellCmd(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !sending && shellCmd && sendCommand()}
                  placeholder="ej: df -h / uptime / ps aux | head -20"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13, fontFamily: 'monospace' }} />
              </div>
            )}
            {selectedType === 'restart_service' && (
              <div style={{ flex: 1, minWidth: 160 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>SERVICIO</label>
                <input value={serviceName} onChange={e => setServiceName(e.target.value)}
                  placeholder="ej: nginx, docker, ssh"
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13 }} />
              </div>
            )}
            <button onClick={sendCommand}
              disabled={sending || !selectedType || (selectedType === 'shell' && !shellCmd) || (selectedType === 'restart_service' && !serviceName)}
              style={{ padding: '8px 18px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: (sending || !selectedType) ? 0.6 : 1, whiteSpace: 'nowrap' }}>
              <Play size={13} /> {sending ? 'Enviando...' : 'Enviar'}
            </button>
            <button onClick={loadHistory} title="Actualizar"
              style={{ padding: '8px 12px', background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: 'var(--ink-2)' }}>
              <RotateCcw size={13} />
            </button>
          </div>
        </div>
      )}

      {commands.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)', fontSize: 13 }}>Sin comandos enviados a este equipo</div>
      ) : (
        <div>
          {commands.map(cmd => {
            const s = STATUS_STYLES[cmd.status] || STATUS_STYLES.pending;
            const isExp = expandedId === cmd.id;
            return (
              <div key={cmd.id} style={{ border: '1px solid var(--border)', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
                <div onClick={() => setExpandedId(isExp ? null : cmd.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', background: 'var(--surface)' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', flex: 1 }}>
                    {TYPE_LABELS[cmd.command_type] || cmd.command_type}
                    {cmd.payload?.cmd && <span style={{ fontWeight: 400, color: 'var(--ink-2)', marginLeft: 8, fontFamily: 'monospace', fontSize: 11 }}>$ {cmd.payload.cmd}</span>}
                    {cmd.payload?.service && <span style={{ fontWeight: 400, color: 'var(--ink-2)', marginLeft: 8, fontSize: 11 }}>{cmd.payload.service}</span>}
                  </span>
                  <span style={{ fontSize: 11, color: s.color, fontWeight: 500 }}>
                    {cmd.status === 'pending' && cmd.approved === false ? 'Requiere aprobación en la app' : s.label}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{new Date(cmd.created_at).toLocaleString('es-AR')}</span>
                  {cmd.status === 'pending' && (
                    <button onClick={e => { e.stopPropagation(); cancelCommand(cmd.id); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 2 }}>
                      <XCircle size={14} />
                    </button>
                  )}
                  <ChevronDown size={13} style={{ color: 'var(--ink-3)', transform: isExp ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </div>
                {isExp && (
                  <div style={{ borderTop: '1px solid var(--bg)', background: 'var(--ink)', padding: 14 }}>
                    {cmd.output ? (
                      <pre style={{ margin: 0, color: 'var(--border)', fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 400, overflowY: 'auto' }}>
                        {cmd.output}
                      </pre>
                    ) : (
                      <div style={{ color: 'var(--ink-2)', fontSize: 12, fontFamily: 'monospace' }}>
                        {cmd.status === 'pending' && cmd.approved === false ? 'Comando activo: requiere aprobación de un admin antes de ejecutarse.' :
                         cmd.status === 'pending' ? 'Esperando que el agente recoja el comando...' :
                         cmd.status === 'running'  ? 'Ejecutando en el equipo remoto...' :
                         cmd.status === 'timeout'  ? 'El comando excedio el tiempo de espera' :
                         cmd.status === 'cancelled'? 'Cancelado' : 'Sin output'}
                      </div>
                    )}
                    {cmd.exit_code !== null && cmd.exit_code !== undefined && (
                      <div style={{ marginTop: 8, fontSize: 11, color: cmd.exit_code === 0 ? 'var(--green)' : '#f87171' }}>
                        exit code: {cmd.exit_code}
                      </div>
                    )}
                    <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-2)' }}>
                      {cmd.created_by_name && <span>Enviado por: <strong style={{ color: 'var(--ink-3)' }}>{cmd.created_by_name}</strong> &middot; </span>}
                      {cmd.completed_at && <span>Completado: {new Date(cmd.completed_at).toLocaleString('es-AR')} &middot; </span>}
                      ID: <span style={{ fontFamily: 'monospace', color: 'var(--ink-2)' }}>{cmd.id.slice(0, 8)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── PolicyComplianceTab ───────────────────────────────────────────────────────
const POLICY_RULE_DEFS = [
  { key: 'password_min_length',      label: 'Longitud mínima de contraseña', group: 'Contraseña',   fmt: v => `${v} chars`,                              check: (a, t) => a >= t },
  { key: 'password_complexity',      label: 'Complejidad requerida',          group: 'Contraseña',   fmt: v => v ? 'Activa' : 'Inactiva',                 check: (a, t) => !!a === !!t },
  { key: 'password_max_age_days',    label: 'Expiración de contraseña',       group: 'Contraseña',   fmt: v => v === 0 ? 'Nunca' : `${v} días`,           check: (a, t) => t === 0 || a === 0 || a <= t },
  { key: 'password_history_count',   label: 'Historial de contraseñas',       group: 'Contraseña',   fmt: v => v ? `${v} últimas` : 'Sin historial',      check: (a, t) => a >= t },
  { key: 'account_lockout_attempts', label: 'Intentos antes del bloqueo',     group: 'Bloqueo',      fmt: v => v === 0 ? 'Desactivado' : `${v} intentos`, check: (a, t) => t === 0 ? a === 0 : (a > 0 && a <= t) },
  { key: 'account_lockout_duration', label: 'Duración del bloqueo',           group: 'Bloqueo',      fmt: v => `${v} min`,                                check: (a, t) => a >= t },
  { key: 'screen_lock_timeout',      label: 'Bloqueo de pantalla',            group: 'Pantalla',     fmt: v => `${v} min`,                                check: (a, t) => a <= t },
  { key: 'screen_lock_require_pass', label: 'Contraseña al desbloquear',      group: 'Pantalla',     fmt: v => v ? 'Requerida' : 'No requerida',          check: (a, t) => !!a === !!t },
  { key: 'disable_usb_storage',      label: 'USB Storage',                    group: 'Seguridad',    fmt: v => v ? 'Bloqueado' : 'Permitido',             check: (a, t) => !!a === !!t },
  { key: 'disable_autorun',          label: 'Autorun',                        group: 'Seguridad',    fmt: v => v ? 'Deshabilitado' : 'Habilitado',        check: (a, t) => !!a === !!t },
  { key: 'require_uac',              label: 'UAC',                            group: 'Seguridad',    fmt: v => v ? 'Activo' : 'Inactivo',                 check: (a, t) => !!a === !!t },
  { key: 'disable_rdp',              label: 'RDP',                            group: 'Seguridad',    fmt: v => v ? 'Deshabilitado' : 'Habilitado',        check: (a, t) => !!a === !!t },
  { key: 'require_defender',         label: 'Windows Defender',               group: 'Seguridad',    fmt: v => v ? 'Activo' : 'Inactivo',                 check: (a, t) => !!a === !!t },
  { key: 'defender_up_to_date',      label: 'Defender actualizado',           group: 'Seguridad',    fmt: v => v ? 'Al día' : 'Desactualizado',           check: (a) => !!a, noTarget: true },
];

function fmtTimeAgo(ts) {
  if (!ts) return null;
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'hace menos de 1 min';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  return `hace ${Math.floor(h / 24)} días`;
}

function PolicyComplianceTab({ asset, user, onAuditComplete }) {
  const [policies, setPolicies] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [auditMsg, setAuditMsg] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const canAudit = (user?.role === 'admin' || user?.role === 'manager' || user?.role === 'technician') && asset.agent_standard;

  useEffect(() => {
    fetch(`${API_URL}/policies/asset/${asset.id}`, { headers })
      .then(r => r.json())
      .then(d => setPolicies(Array.isArray(d) ? d : []))
      .catch(() => setPolicies([]));
  }, [asset.id]); // eslint-disable-line

  // Merge rules from all effective policies
  const effectiveRules = (() => {
    if (!policies?.length) return null;
    const merged = {};
    for (const p of policies) {
      const r = typeof p.rules === 'string' ? JSON.parse(p.rules) : (p.rules || {});
      Object.assign(merged, r);
    }
    return Object.keys(merged).length > 0 ? merged : null;
  })();

  const compliance = asset.policy_compliance
    ? (typeof asset.policy_compliance === 'string' ? JSON.parse(asset.policy_compliance) : asset.policy_compliance)
    : null;

  const triggerAudit = async () => {
    setAuditing(true);
    setAuditMsg({ type: 'info', text: 'Enviando solicitud de auditoría al agente...' });
    try {
      const r = await fetch(`${API_URL}/assets/${asset.id}/actions`, {
        method: 'POST', headers,
        body: JSON.stringify({ action_type: 'get_policies' }),
      });
      const action = await r.json();
      if (!action.id) throw new Error(action.error || 'Error creando acción');
      setAuditMsg({ type: 'info', text: 'Esperando que el agente ejecute la auditoría...' });
      const iv = setInterval(async () => {
        try {
          const cr = await fetch(`${API_URL}/assets/${asset.id}/actions`, { headers: { Authorization: `Bearer ${token}` } });
          const list = await cr.json();
          const found = Array.isArray(list) && list.find(a => a.id === action.id);
          if (found && ['completed', 'failed'].includes(found.status)) {
            clearInterval(iv);
            setAuditing(false);
            if (found.status === 'completed') {
              setAuditMsg({ type: 'ok', text: '✅ Auditoría completada. Actualizando...' });
              onAuditComplete();
            } else {
              setAuditMsg({ type: 'err', text: `❌ El agente reportó un error al auditar las políticas` });
            }
          }
        } catch {}
      }, 3000);
      // Timeout a los 5 minutos (el agente standard puede tardar hasta el próximo ciclo)
      setTimeout(() => {
        clearInterval(iv);
        if (auditing) {
          setAuditing(false);
          setAuditMsg({ type: 'err', text: '⏰ Sin respuesta del agente. Se ejecutará en el próximo ciclo de escaneo (máx. 60 min).' });
        }
      }, 300000);
    } catch (err) {
      setAuditMsg({ type: 'err', text: `❌ ${err.message}` });
      setAuditing(false);
    }
  };

  // Compute rows for the table
  const rows = POLICY_RULE_DEFS.map(def => {
    const actualRaw = compliance ? compliance[def.key] : undefined;
    const targetRaw = effectiveRules ? effectiveRules[def.key] : undefined;
    const hasActual = actualRaw !== undefined && actualRaw !== null;
    const hasTarget = !def.noTarget && targetRaw !== undefined && targetRaw !== null;
    let status = 'unknown'; // -, ok, fail
    if (hasActual) {
      if (hasTarget) {
        status = def.check(actualRaw, targetRaw) ? 'ok' : 'fail';
      } else if (def.noTarget) {
        status = def.check(actualRaw) ? 'ok' : 'fail';
      }
    }
    return { ...def, actualRaw, targetRaw, hasActual, hasTarget, status };
  });

  const checked = rows.filter(r => r.status !== 'unknown');
  const passed  = checked.filter(r => r.status === 'ok').length;
  const failed  = checked.filter(r => r.status === 'fail').length;
  const score   = checked.length > 0 ? Math.round((passed / checked.length) * 100) : null;

  const scoreColor = score === null ? 'var(--ink-2)' : score >= 80 ? 'var(--green)' : score >= 60 ? 'var(--amber)' : 'var(--red)';
  const scoreBg    = score === null ? 'var(--bg)' : score >= 80 ? 'var(--green-dim)' : score >= 60 ? 'var(--amber-dim)' : 'var(--red-dim)';

  const groups = [...new Set(POLICY_RULE_DEFS.map(d => d.group))];

  return (
    <div>
      {/* Header strip */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Score */}
          {compliance && (
            <div style={{ background: scoreBg, border: `1px solid ${scoreColor}33`, borderRadius: 10, padding: '10px 18px', textAlign: 'center', minWidth: 90 }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{score ?? '—'}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: scoreColor, textTransform: 'uppercase', letterSpacing: 1 }}>/ 100</div>
              <div style={{ fontSize: 11, color: scoreColor, marginTop: 2 }}>
                {passed} ok · {failed} {failed !== 1 ? 'fallos' : 'fallo'}
              </div>
            </div>
          )}
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)' }}>Estado de políticas</div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
              {compliance
                ? `Última auditoría: ${fmtTimeAgo(asset.policy_compliance_at)} · ${asset.policy_compliance_at ? new Date(asset.policy_compliance_at).toLocaleString('es-AR') : ''}`
                : 'Sin auditoría registrada para este equipo'
              }
            </div>
            {policies !== null && policies.length === 0 && (
              <div style={{ fontSize: 11, color: 'var(--amber)', marginTop: 3 }}>
                ⚠ Sin políticas asignadas — los indicadores muestran el estado sin objetivo de cumplimiento
              </div>
            )}
            {policies !== null && policies.length > 0 && (
              <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 3 }}>
                Políticas aplicadas: {policies.map(p => p.name).join(', ')}
              </div>
            )}
          </div>
        </div>

        {canAudit && (
          <button
            onClick={triggerAudit}
            disabled={auditing}
            style={{
              padding: '9px 20px', background: auditing ? 'var(--ink-3)' : 'var(--accent)', color: 'var(--surface)',
              border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13,
              cursor: auditing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            <Shield size={14} />
            {auditing ? 'Auditando...' : 'Auditar ahora'}
          </button>
        )}
      </div>

      {auditMsg && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: auditMsg.type === 'ok' ? 'var(--green-dim)' : auditMsg.type === 'err' ? 'var(--red-dim)' : 'var(--accent-dim)',
          color: auditMsg.type === 'ok' ? 'var(--green)' : auditMsg.type === 'err' ? 'var(--red)' : 'var(--accent)',
          border: `1px solid ${auditMsg.type === 'ok' ? '#86efac' : auditMsg.type === 'err' ? 'var(--red-dim)' : 'var(--accent-dim)'}`,
        }}>
          {auditMsg.type === 'info' && <span style={{ marginRight: 8 }}>⏳</span>}
          {auditMsg.text}
        </div>
      )}

      {!compliance && !auditing && (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--ink-3)', border: '1px dashed var(--border)', borderRadius: 10 }}>
          <Shield size={36} style={{ opacity: 0.2, display: 'block', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Sin datos de políticas</div>
          <div style={{ fontSize: 12 }}>Hacé clic en "Auditar ahora" para leer el estado actual del equipo</div>
        </div>
      )}

      {compliance && (
        <div>
          {groups.map(group => {
            const groupRows = rows.filter(r => r.group === group);
            return (
              <div key={group} style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                  {group}
                </div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-subtle)' }}>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, width: 32 }}></th>
                        <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--ink-2)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Regla</th>
                        <th style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 600, color: 'var(--ink-2)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Estado actual</th>
                        {effectiveRules && (
                          <th style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 600, color: 'var(--ink-2)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Objetivo</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {groupRows.map((row, i) => {
                        const icon = row.status === 'ok' ? '✅' : row.status === 'fail' ? '❌' : '—';
                        const actualDisplay = row.hasActual ? row.fmt(row.actualRaw) : '—';
                        const targetDisplay = row.hasTarget ? row.fmt(row.targetRaw) : (row.noTarget ? null : '—');
                        return (
                          <tr key={row.key} style={{ borderTop: i > 0 ? '1px solid var(--bg)' : 'none', background: row.status === 'fail' ? '#fef9f9' : '#fff' }}>
                            <td style={{ padding: '10px 14px', fontSize: 16, textAlign: 'center', width: 32 }}>{icon}</td>
                            <td style={{ padding: '10px 14px', color: 'var(--ink-2)', fontWeight: 500 }}>{row.label}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, color: row.status === 'fail' ? 'var(--red)' : row.status === 'ok' ? 'var(--green)' : 'var(--ink-2)' }}>
                              {actualDisplay}
                            </td>
                            {effectiveRules && (
                              <td style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--ink-2)', fontSize: 12 }}>
                                {targetDisplay ?? <span style={{ color: 'var(--border-2)' }}>—</span>}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
            Datos capturados el {new Date(compliance.captured_at || asset.policy_compliance_at).toLocaleString('es-AR')}
          </div>
        </div>
      )}
    </div>
  );
}

// Botón de renombrado de equipo (admin) — encola el rename remoto + reinicio; el backend
// reconcilia el duplicado con el asset original (conserva historial). Ver services/assetRename.js.
function RenameButton({ asset, onRenamed }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [pending, setPending] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/assets/${asset.id}/rename`, { headers })
      .then(r => r.json()).then(setPending).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const valid = /^[A-Za-z0-9-]{1,15}$/.test(name) && !/^-|-$/.test(name)
    && name.toLowerCase() !== (asset.hostname || '').toLowerCase();

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`${API_URL}/assets/${asset.id}/rename`, {
        method: 'POST', headers, body: JSON.stringify({ new_hostname: name }) });
      const d = await r.json();
      if (!r.ok) setMsg({ err: true, text: d.error || 'Error' });
      else { setMsg({ err: false, text: d.message }); setPending({ status: 'pending', new_hostname: name }); onRenamed && onRenamed(); }
    } catch { setMsg({ err: true, text: 'Error de red' }); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => { setOpen(true); setName(''); setMsg(null); }} title="Renombrar equipo"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 2, display: 'inline-flex' }}>
        <Pencil size={13} />
      </button>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 8, padding: 20, width: 400, maxWidth: '92vw', border: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Renombrar equipo</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
              Actual: <span className="mono">{asset.hostname}</span>. El equipo se renombrará y <b>reiniciará</b>. El historial se conserva: la ficha se reconcilia automáticamente cuando el equipo reporte el nombre nuevo.
            </div>
            {pending?.status === 'pending' && (
              <div style={{ fontSize: 12, color: 'var(--amber)', marginBottom: 10 }}>
                ⏳ Renombrado en curso → <span className="mono">{pending.new_hostname}</span> (esperando que el equipo reinicie y reporte).
              </div>
            )}
            <input autoFocus value={name} onChange={e => setName(e.target.value.toUpperCase())} placeholder="CDM-PC0020"
              style={{ width: '100%', padding: '8px 10px', border: '1.5px solid var(--border-2)', borderRadius: 6, fontFamily: 'monospace', fontSize: 14, boxSizing: 'border-box', background: 'var(--surface)', color: 'var(--ink)' }} />
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>1–15 caracteres · letras, números y guiones · sin guion al inicio o fin.</div>
            {msg && <div style={{ fontSize: 12, color: msg.err ? 'var(--red)' : 'var(--green)', marginTop: 8 }}>{msg.text}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setOpen(false)}>Cerrar</button>
              <button className="btn btn-primary btn-sm" disabled={!valid || busy} onClick={submit}>{busy ? 'Encolando…' : 'Renombrar y reiniciar'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('hardware');
  const [assetIncidents, setAssetIncidents] = useState([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [note, setNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [noteSuccess, setNoteSuccess] = useState('');
  const [assigningInventory, setAssigningInventory] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingLifecycle, setSavingLifecycle] = useState(false);
  const [locations, setLocations] = useState([]);
  const [savingLocation, setSavingLocation] = useState(false);
  const [wifiProfiles, setWifiProfiles] = useState(null);
  const [wifiVisible, setWifiVisible] = useState(null);
  const [loadingWifi, setLoadingWifi] = useState(false);
  const [rdMenuOpen, setRdMenuOpen] = useState(false);
  const [rdCopied, setRdCopied] = useState(false);
  const [savingCriticidad, setSavingCriticidad] = useState(false);
  const [criticidadForm, setCriticidadForm] = useState(null);
  const [swCveMap, setSwCveMap] = useState({});

  // Cerrar menú RustDesk al hacer click fuera
  useEffect(() => {
    if (!rdMenuOpen) return;
    const handler = (e) => {
      if (!e.target.closest('[data-rd-menu]')) setRdMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [rdMenuOpen]);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadAsset = () => {
    api.getAsset(id)
      .then(a => {
        setAsset(a);
        setCriticidadForm({
          ambiente: a.ambiente || '',
          criticidad: a.criticidad || '',
          drp: a.drp || false,
          disponibilidad_servicio: a.disponibilidad_servicio || '',
          horario_critico: a.horario_critico || '',
          impacto_negocio: a.impacto_negocio || '',
          rto_horas: a.rto_horas != null ? a.rto_horas : '',
          alta_disponibilidad: a.alta_disponibilidad || false,
        });
      })
      .catch(() => navigate('/assets'))
      .finally(() => setLoading(false));
  };

  const loadIncidents = () => {
    setLoadingIncidents(true);
    incidentApi.getByAsset(id)
      .then(data => { setAssetIncidents(Array.isArray(data) ? data : []); })
      .catch(() => setAssetIncidents([]))
      .finally(() => setLoadingIncidents(false));
  };

  useEffect(() => {
    const SEV_RANK = { critical: 3, high: 2, medium: 1, low: 0 };
    Promise.all([
      fetch(`${API_URL}/nvd/asset/${id}`, { headers }).then(r => r.ok ? r.json() : {}),
      fetch(`${API_URL}/hygiene/asset/${id}`, { headers }).then(r => r.ok ? r.json() : []),
    ]).then(([nvd, hygiene]) => {
      const map = {};
      const set = (name, sev, type) => {
        if (!map[name] || (SEV_RANK[sev] || 0) > (SEV_RANK[map[name].sev] || 0))
          map[name] = { sev, type };
      };
      (nvd.affected_software || []).forEach(({ software_name, cves }) => {
        (cves || []).forEach(cve => {
          const sev = (cve.cvss_severity || 'medium').toLowerCase();
          set(software_name, sev, 'cve');
        });
      });
      (Array.isArray(hygiene) ? hygiene : []).forEach(({ software_name, risk_level }) => {
        set(software_name, risk_level, 'risk');
      });
      setSwCveMap(map);
    }).catch(() => {});
  }, [id]); // eslint-disable-line

  useEffect(() => {
    loadAsset();
    loadProfiles();
    fetch(`${API_URL}/locations`, { headers })
      .then(r => r.json())
      .then(data => setLocations(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [id]);

  const loadProfiles = () => {
    fetch(`${API_URL}/baseline/profiles`, { headers })
      .then(r => r.json())
      .then(data => setProfiles(Array.isArray(data) ? data : []))
      .catch(() => {});
    fetch(`${API_URL}/baseline/assets/${id}/profile`, { headers })
      .then(r => r.json())
      .then(data => setCurrentProfile(data))
      .catch(() => {});
  };
  useEffect(() => { if (activeTab === 'incidentes') loadIncidents(); }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'red' || wifiProfiles !== null) return;
    setLoadingWifi(true);
    Promise.all([
      fetch(`${API_URL}/wifi/asset/${id}`, { headers }).then(r => r.json()).catch(() => ({})),
      fetch(`${API_URL}/wifi/asset/${id}/visible`, { headers }).then(r => r.json()).catch(() => []),
    ]).then(([prof, vis]) => {
      setWifiProfiles(prof);
      setWifiVisible(Array.isArray(vis) ? vis : []);
    }).finally(() => setLoadingWifi(false));
  }, [activeTab]);

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar el activo "${asset.hostname}"?`)) return;
    setDeleting(true);
    try { await api.deleteAsset(id); navigate('/assets'); }
    catch (e) { alert(e.message); setDeleting(false); }
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    setAddingNote(true);
    try {
      await fetch(`${API_URL}/assets/${id}/history/note`, {
        method: 'POST', headers, body: JSON.stringify({ note })
      });
      setNote('');
      setNoteSuccess('Nota agregada correctamente.');
      setTimeout(() => setNoteSuccess(''), 3000);
      loadAsset();
    } catch (e) { alert(e.message); }
    finally { setAddingNote(false); }
  };

  const handleSaveProfile = async (profileId) => {
    setSavingProfile(true);
    try {
      await fetch(`${API_URL}/baseline/assets/${id}/profile`, {
        method: 'PUT', headers,
        body: JSON.stringify({ profile_id: profileId || null })
      });
      setCurrentProfile(profiles.find(p => p.id === profileId) || null);
    } catch(e) { alert(e.message); }
    finally { setSavingProfile(false); }
  };

  const handleAssignInventory = async () => {
    setAssigningInventory(true);
    try {
      const r = await fetch(`${API_URL}/inventory/assign/${id}`, {
        method: 'POST', headers, body: JSON.stringify({ asset_type: asset.asset_type || 'pc' })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      loadAsset();
    } catch (e) { alert(e.message); }
    finally { setAssigningInventory(false); }
  };

  const handleLocationChange = async (newLocationId) => {
    setSavingLocation(true);
    try {
      const r = await fetch(`${API_URL}/assets/${id}/location`, {
        method: 'PATCH', headers, body: JSON.stringify({ location_id: newLocationId || null })
      });
      if (!r.ok) throw new Error((await r.json()).error);
      loadAsset();
    } catch (e) { alert(e.message); }
    finally { setSavingLocation(false); }
  };

  const handleLifecycleChange = async (newStatus) => {
    setSavingLifecycle(true);
    try {
      const r = await fetch(`${API_URL}/assets/${id}/lifecycle`, {
        method: 'PATCH', headers, body: JSON.stringify({ lifecycle_status: newStatus })
      });
      if (!r.ok) throw new Error((await r.json()).error);
      loadAsset();
    } catch (e) { alert(e.message); }
    finally { setSavingLifecycle(false); }
  };

  const handleCriticidadSave = async () => {
    setSavingCriticidad(true);
    try {
      const r = await fetch(`${API_URL}/assets/${id}/criticidad`, {
        method: 'PATCH', headers, body: JSON.stringify(criticidadForm)
      });
      if (!r.ok) throw new Error((await r.json()).error);
      loadAsset();
      setCriticidadForm(null);
    } catch (e) { alert(e.message); }
    finally { setSavingCriticidad(false); }
  };

  const handlePrintLabel = async () => {
    // Se pide un token acotado (2 min, solo esta etiqueta) en vez de mandar el JWT de
    // sesión por la URL, que quedaba en los logs y en el historial.
    const r = await fetch(`${API_URL}/inventory/label-token/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { alert('No se pudo generar la etiqueta.'); return; }
    const { token: etiqueta } = await r.json();
    window.open(`${API_URL}/inventory/label/${id}?token=${etiqueta}`, '_blank');
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!asset) return null;

  const ips = Array.isArray(asset.ip_addresses) ? asset.ip_addresses : JSON.parse(asset.ip_addresses || '[]');
  const macs = Array.isArray(asset.mac_addresses) ? asset.mac_addresses : JSON.parse(asset.mac_addresses || '[]');
  const pendingUpdates = Array.isArray(asset.pending_updates) ? asset.pending_updates : JSON.parse(asset.pending_updates || '[]');
  const grade = GRADE_MAP[asset.security_grade];

  return (
    <div className="asset-detail">
      <div className="detail-breadcrumb">
        <Link to="/assets" className="breadcrumb-link">Inventario</Link>
        <span className="breadcrumb-sep">›</span>
        <span className="mono">{asset.hostname}</span>
      </div>

      <div className="detail-header">
        <div className="detail-host-info">
          <div className="host-avatar" style={{ background: grade?.bg || 'var(--blue-light)', color: grade?.color || 'var(--blue)' }}>
            {asset.hostname?.[0]?.toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            {/* Fila superior: nombre + parches + score + agente */}
            <div className="detail-title-row">
              <span className="detail-hostname mono">{asset.hostname}</span>
              {user?.role === 'admin' && /windows/i.test(asset.os_name || '') && (
                <RenameButton asset={asset} onRenamed={() => loadAsset()} />
              )}
              {asset.patches_up_to_date === false && (
                <span className="detail-chip detail-chip-warn">⚠ Parches pendientes</span>
              )}
              {asset.patches_up_to_date === true && (
                <span className="detail-chip detail-chip-ok">✓ Al día</span>
              )}
              {grade && (
                <span className="detail-chip detail-chip-score">
                  <Shield size={10} />
                  {asset.security_score} · {grade.label}
                </span>
              )}
              {asset.agent_standard && <span className="detail-chip detail-chip-agent">STD</span>}
              {asset.agent_cc && <span className="detail-chip" style={{ background: '#fce7f3', color: '#9d174d' }}>C&amp;C</span>}
              {(user?.role === 'admin' || user?.role === 'technician') && <AgentCredential assetId={asset.id} />}
              {user?.role === 'admin' && <AdminLocalReveal assetId={asset.id} />}
            </div>
            {/* Fila inferior: usuario, email, ubicación, status */}
            <div className="detail-meta">
              {asset.user_name && <span>{asset.user_name}</span>}
              {asset.user_email && <><span className="meta-sep">·</span><span style={{color:'var(--blue)'}}>{asset.user_email}</span></>}
              {asset.location_name && <><span className="meta-sep">·</span><span>{asset.location_name}</span></>}
              {/* status solo si no es 'warning' — ese caso ya está en el chip de arriba */}
              {asset.status && asset.status !== 'warning' && (
                <><span className="meta-sep">·</span>{statusBadge(asset.status)}</>
              )}
              {asset.criticidad && (() => {
                const CRIT = { critica: ['#FDECEB','#C0392B','🔴 Crítica'], alta: ['#FFF3E8','#D45D00','🟠 Alta'], media: ['#FFFDE7','#856404','🟡 Media'], baja: ['#EAF4ED','#1A6E2E','🟢 Baja'] };
                const [bg, color, label] = CRIT[asset.criticidad] || [];
                return bg ? (
                  <><span className="meta-sep">·</span>
                  <span style={{ color, fontSize: 11, fontWeight: 600 }}>{label}</span></>
                ) : null;
              })()}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Selector de ubicación */}
          {(user?.role === 'admin' || user?.role === 'technician') && locations.length > 0 && (
            <select
              value={asset.location_id || ''}
              onChange={e => handleLocationChange(e.target.value)}
              disabled={savingLocation}
              style={{ padding: '5px 10px', borderRadius: 8, fontSize: 12, border: '1.5px solid var(--border-2)', color: 'var(--ink-2)', background: 'var(--surface)', cursor: 'pointer', height: '36px' }}>
              <option value="">📍 Sin ubicación</option>
              {locations
                .filter(l => (asset.asset_type === 'virtual' || asset.asset_type === 'cloud' || asset.asset_type === 'container')
                  ? true
                  : !CLOUD_LOCATION_NAMES.includes(l.name)
                )
                .map(l => <option key={l.id} value={l.id}>📍 {l.name}</option>)
              }
            </select>
          )}
          {/* Selector lifecycle status */}
          {(user?.role === 'admin' || user?.role === 'technician') && (() => {
            const lc = LIFECYCLE_OPTIONS.find(o => o.value === (asset.lifecycle_status || 'active')) || LIFECYCLE_OPTIONS[0];
            return (
              <select
                value={asset.lifecycle_status || 'active'}
                onChange={e => handleLifecycleChange(e.target.value)}
                disabled={savingLifecycle}
                style={{
                  padding: '5px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  border: `1.5px solid ${lc.color}`, color: lc.color, background: lc.bg,
                  cursor: 'pointer', height: '36px', appearance: 'none',
                  paddingRight: 24, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='${encodeURIComponent(lc.color)}'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center'
                }}>
                {LIFECYCLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            );
          })()}
          {/* Selector de perfil baseline — solo para workstations/laptops físicos */}
          {user?.role === 'admin' && asset.asset_type === 'physical' && !['server'].includes(asset.device_subtype) && (
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
              <select
                value={currentProfile?.id || ""}
                onChange={e => handleSaveProfile(e.target.value || null)}
                disabled={savingProfile}
                style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 12, color: "var(--gray-700)", background: "var(--surface)", cursor: "pointer", height: "36px" }}>
                <option value="">Sin perfil baseline</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {savingProfile && <span style={{ fontSize: 11, color: "var(--gray-500)" }}>Guardando...</span>}
            </div>
          )}
          {/* Número de inventario */}
          {asset.inventory_number ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#E6F1FB', border: '1px solid #90C4E8', borderRadius: 8, padding: '6px 12px' }}>
              <Tag size={14} color="var(--accent)" />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', fontFamily: 'monospace' }}>{asset.inventory_number}</span>
              <button onClick={handlePrintLabel} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 4, color: 'var(--accent)' }} title="Imprimir etiqueta">
                <Printer size={14} />
              </button>
            </div>
          ) : (
            <button className="btn btn-secondary" onClick={handleAssignInventory} disabled={assigningInventory} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <Tag size={14} /> {assigningInventory ? 'Asignando...' : 'N° inventario'}
            </button>
          )}
          {/* Tags del equipo (asignar/quitar manual) */}
          {(user?.role === 'admin' || user?.role === 'technician') && <TagsSection assetId={asset.id} />}
          {/* Acceso remoto — RustDesk (reemplaza Quick Assist) */}
          {asset.rustdesk_id && (user?.role === 'admin' || user?.role === 'technician') && (
            <div style={{ position: 'relative' }} data-rd-menu>
              <button
                className="btn btn-secondary"
                onClick={() => { setRdMenuOpen(o => !o); }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                title="Acceso remoto vía RustDesk"
              >
                <Monitor size={14} />
                RustDesk
              </button>
              {rdMenuOpen && (
                <div style={{
                  position: 'absolute', top: 42, right: 0, zIndex: 200,
                  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 14, minWidth: 280,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Monitor size={15} style={{ color: '#0d9488' }} />
                      RustDesk
                    </span>
                    <button onClick={() => setRdMenuOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--ink-3)', lineHeight: 1 }}>×</button>
                  </div>

                  {/* ID de conexión + copiar */}
                  <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-subtle)', border: '1px solid var(--border)', marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>ID de conexión</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16, color: 'var(--ink)' }}>{asset.rustdesk_id}</span>
                      <button onClick={() => { navigator.clipboard?.writeText(asset.rustdesk_id); setRdCopied(true); setTimeout(() => setRdCopied(false), 1600); }}
                        style={{ fontSize: 12, fontWeight: 600, padding: '5px 11px', border: '1px solid var(--border-2)', borderRadius: 7, background: 'var(--surface)', cursor: 'pointer', color: rdCopied ? 'var(--green)' : 'var(--ink-2)' }}>
                        {rdCopied ? 'Copiado ✓' : 'Copiar'}
                      </button>
                    </div>
                    {(asset.rustdesk_relay || asset.rustdesk_version) && (
                      <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 4 }}>
                        {asset.rustdesk_relay ? `relay ${asset.rustdesk_relay}` : ''}{asset.rustdesk_relay && asset.rustdesk_version ? ' · ' : ''}{asset.rustdesk_version ? `v${asset.rustdesk_version}` : ''}
                      </div>
                    )}
                  </div>

                  {/* Conectar con el cliente RustDesk */}
                  <a
                    href={`rustdesk://${asset.rustdesk_id}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                      borderRadius: 8, background: '#0d9488', border: '1px solid #0d9488',
                      textDecoration: 'none', cursor: 'pointer',
                    }}
                  >
                    <span style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Monitor size={16} color="#fff" />
                    </span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--surface)' }}>Conectar con RustDesk</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 1 }}>
                        Abre el cliente RustDesk hacia {asset.hostname}
                      </div>
                    </div>
                  </a>

                  <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 10, borderTop: '1px solid var(--bg)', paddingTop: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <i className="ti ti-info-circle" style={{ fontSize: 11 }} />
                    Requiere el cliente RustDesk instalado en tu equipo
                  </div>
                </div>
              )}
            </div>
          )}
          {user?.role === 'admin' && (
            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Eliminando...' : '✕ Eliminar'}
            </button>
          )}
        </div>
      </div>

      <div className="detail-tabs">
        {['hardware', 'red', asset.agent_type === 'android' ? 'apps' : 'software', 'costos', 'historial', 'incidentes', 'criticidad', ...(asset.asset_type === 'physical' && asset.agent_type !== 'android' ? ['usb', 'salud'] : []), ...(asset.agent_cc ? ['comandos'] : []), ...(asset.agent_standard && (asset.os_name||'').toLowerCase().includes('windows') ? ['politicas'] : [])].map(tab => (
          <button key={tab} className={`tab-btn${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab === 'hardware'    && <Cpu           size={13} style={{ marginRight: 4 }} />}
            {tab === 'red'         && <Wifi          size={13} style={{ marginRight: 4 }} />}
            {tab === 'software'    && <Package       size={13} style={{ marginRight: 4 }} />}
            {tab === 'apps'        && <Package       size={13} style={{ marginRight: 4 }} />}
            {tab === 'costos'      && <DollarSign    size={13} style={{ marginRight: 4 }} />}
            {tab === 'historial'   && <Clock         size={13} style={{ marginRight: 4 }} />}
            {tab === 'incidentes'  && <AlertTriangle size={13} style={{ marginRight: 4 }} />}
            {tab === 'criticidad'  && <Shield        size={13} style={{ marginRight: 4 }} />}
            {tab === 'usb'         && <HardDrive     size={13} style={{ marginRight: 4 }} />}
            {tab === 'salud'       && <Activity      size={13} style={{ marginRight: 4 }} />}
            {tab === 'acciones'    && <i className="ti ti-refresh" style={{ fontSize: 13, marginRight: 4 }} />}
            {tab === 'comandos'    && <Terminal      size={13} style={{ marginRight: 4 }} />}
            {tab === 'politicas'   && <Shield        size={13} style={{ marginRight: 4 }} />}
            {tab === 'usb' ? 'USB' : tab === 'salud' ? 'Salud HW' : tab === 'acciones' ? 'Acciones' : tab === 'criticidad' ? 'Criticidad' : tab === 'apps' ? 'Apps' : tab === 'politicas' ? 'Políticas' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'historial' && asset.scan_history?.length > 0 && (
              <span style={{marginLeft:6, background:'var(--blue-light)', color:'var(--blue)', borderRadius:10, padding:'1px 6px', fontSize:10}}>
                {asset.scan_history.length}
              </span>
            )}          </button>
        ))}
      </div>

      {activeTab === 'hardware' && (
        <div className="card detail-card">
          <div className="detail-section-title">Sistema operativo</div>
          <div className="fields-grid">
            <Field label="SO" value={asset.os_name} />
            <Field label="Versioon" value={asset.os_version} />
            <Field label="Build" value={asset.os_build} />
            <Field label="Nivel de parche" value={asset.patch_level} />
            <Field label="Actualizaciones pendientes" value={asset.pending_updates_count != null ? `${asset.pending_updates_count} pendientes` : '\u2014'} />
          </div>

          {pendingUpdates.length > 0 && (
            <>
              <div className="detail-section-title" style={{ marginTop: 20 }}>Actualizaciones pendientes ({asset.pending_updates_count})</div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Título</th><th style={{width:100}}>KB</th><th style={{width:100}}>Severidad</th></tr></thead>
                  <tbody>
                    {pendingUpdates.map((u, i) => (
                      <tr key={i}>
                        <td style={{fontSize:13}}>{u.title}</td>
                        <td><span className="mono" style={{fontSize:11}}>{u.kb || '\u2014'}</span></td>
                        <td>
                          <span style={{
                            background: u.severity === 'Critical' ? 'var(--red-dim)' : u.severity === 'Important' ? 'var(--amber-dim)' : 'var(--bg-subtle)',
                            color: u.severity === 'Critical' ? 'var(--red)' : u.severity === 'Important' ? '#D45D00' : 'var(--ink-2)',
                            padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500
                          }}>{u.severity || 'N/A'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {(asset.geo_city || asset.public_ip) && (
            <>
              <div className="detail-section-title" style={{ marginTop: 20 }}>Geolocalizacioon</div>
              <div className="fields-grid">
                <Field label="IP Pública" value={asset.public_ip} />
                <Field label="País" value={asset.geo_country} />
                <Field label="Provincia/Estado" value={asset.geo_region} />
                <Field label="Ciudad" value={asset.geo_city} />
                <Field label="ISP" value={asset.geo_isp} />
                {asset.geo_lat && <Field label="Coordenadas" value={`${asset.geo_lat}, ${asset.geo_lon}`} />}
              </div>
            </>
          )}

          {asset.latitude && asset.longitude && (
            <>
              <div className="detail-section-title" style={{ marginTop: 20 }}>Ubicación</div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 10 }}>
                  <div style={{ fontSize: 13, color: '#495057' }}>
                    <span style={{ fontFamily: 'monospace' }}>{asset.latitude.toFixed(5)}, {asset.longitude.toFixed(5)}</span>
                    {asset.location_city && (
                      <span style={{ marginLeft: 10, color: '#6c757d' }}>
                        📍 {asset.location_city}{asset.location_country ? `, ${asset.location_country}` : ''}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: asset.location_source === 'gps' ? 'var(--green-dim)' : '#F1F3F5', color: asset.location_source === 'gps' ? 'var(--green)' : '#495057', border: `1px solid ${asset.location_source === 'gps' ? 'var(--green)' : '#dee2e6'}` }}>
                    {asset.location_source === 'gps' ? '📡 GPS/WiFi' : '🌐 IP'}
                    {asset.location_accuracy ? ` ±${asset.location_accuracy}m` : ''}
                  </div>
                  <a
                    href={`https://www.google.com/maps?q=${asset.latitude},${asset.longitude}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none', marginLeft: 'auto' }}
                  >
                    Ver en Google Maps ↗
                  </a>
                </div>
                <iframe
                  title="mapa"
                  width="100%" height="200"
                  style={{ border: 'none', borderRadius: 8, display: 'block' }}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${asset.longitude - 0.01},${asset.latitude - 0.01},${asset.longitude + 0.01},${asset.latitude + 0.01}&layer=mapnik&marker=${asset.latitude},${asset.longitude}`}
                />
              </div>
            </>
          )}

          <div className="detail-section-title" style={{ marginTop: 20 }}>Hardware</div>
          {asset.notes && (
            <div style={{ background: 'var(--amber-dim)', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: 'var(--amber)' }}>
              📝 {asset.notes}
            </div>
          )}
          <div className="fields-grid">
            <Field label="Fabricante" value={asset.manufacturer} />
            <Field label="Modelo" value={asset.model} />
            {asset.device_subtype && <Field label="Tipo de dispositivo" value={asset.device_subtype} />}
            <Field label="Procesador" value={asset.cpu} />
            <Field label="Memoria RAM" value={asset.ram_gb ? `${asset.ram_gb} GB` : null} />
            <Field label="Almacenamiento" value={asset.disk_info} />
            <Field label="BIOS / UEFI" value={asset.bios_version} />
            <Field label="Motherboard" value={asset.motherboard} />
            <Field label="Tarjeta gráfica" value={Array.isArray(asset.gpu) && asset.gpu.length > 0 ? asset.gpu.join(' / ') : null} />
            <Field label="Audio" value={Array.isArray(asset.audio) && asset.audio.length > 0 ? asset.audio.join(' / ') : null} />
            <Field label="Nro. de serie" value={asset.serial_number} />
            <Field label="Nro. de producto" value={asset.product_number} />
            <Field label="UUID" value={asset.device_uuid} />
          </div>


          {/* \u2500\u2500 Secci\u00f3n exclusiva Android \u2500\u2500 */}
          {asset.agent_type === 'android' && (() => {
            const md = asset.agent_metadata || {};
            const bat  = md.battery  || {};
            const stor = md.storage  || {};
            const sec  = md.security || {};
            const ph   = md.phone    || null;
            const loc  = md.location || null;
            const apps = Array.isArray(md.installed_apps) ? md.installed_apps : [];
            const boolIcon = v => v === true ? '\u2713' : v === false ? '\u2717' : '\u2014';

            // Compliance score
            let cscore = 0;
            if (md.is_device_admin) cscore += 25;
            if (sec.encryption_status === 'encrypted') cscore += 25;
            if (sec.screen_lock_enabled) cscore += 25;
            if (md.security_patch_level) {
              const days = (Date.now() - new Date(md.security_patch_level).getTime()) / 86400000;
              if (days < 90) cscore += 25;
            }
            const cscoreColor = cscore >= 75 ? 'var(--green)' : cscore >= 50 ? 'var(--amber)' : 'var(--red)';
            const cscoreBg    = cscore >= 75 ? 'var(--green-dim)' : cscore >= 50 ? 'var(--amber-dim)' : 'var(--red-dim)';
            const cscoreLabel = cscore >= 75 ? 'Cumple' : cscore >= 50 ? 'Parcial' : 'No cumple';

            return (
              <>
                {/* Compliance Score */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: cscoreBg, borderRadius: 8, padding: '8px 16px', border: `1px solid ${cscoreColor}22` }}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: cscoreColor, fontVariantNumeric: 'tabular-nums' }}>{cscore}</span>
                    <div>
                      <div style={{ fontSize: 10, color: cscoreColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Compliance</div>
                      <div style={{ fontSize: 11, color: cscoreColor }}>{cscoreLabel}</div>
                    </div>
                  </div>
                  <a href="/mdm" style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--blue)', textDecoration: 'none', fontWeight: 600 }}>
                    \u2192 Gestionar en MDM
                  </a>
                </div>

                {/* Bater\u00eda & almacenamiento */}
                <div className="detail-section-title" style={{ marginTop: 20 }}>Bater\u00eda y Almacenamiento</div>
                <div className="fields-grid">
                  <Field label="Bater\u00eda"         value={bat.level != null ? `${bat.level}%` : null} />
                  <Field label="Estado carga"    value={bat.is_charging ? 'Cargando' : 'Descargando'} />
                  <Field label="Salud bater\u00eda"   value={bat.health} />
                  <Field label="Tecnolog\u00eda"      value={bat.technology} />
                  <Field label="Almacenamiento total" value={stor.total_gb ? `${stor.total_gb.toFixed(0)} GB` : null} />
                  <Field label="Espacio libre"   value={stor.free_gb  ? `${stor.free_gb.toFixed(0)} GB`  : null} />
                  <Field label="Espacio usado"   value={stor.used_gb  ? `${stor.used_gb.toFixed(0)} GB`  : null} />
                  <Field label="RAM disponible"  value={md.available_ram_gb ? `${md.available_ram_gb.toFixed(1)} GB` : null} />
                </div>

                {/* Seguridad Android */}
                <div className="detail-section-title" style={{ marginTop: 20 }}>Seguridad del dispositivo</div>
                <div className="fields-grid">
                  <Field label="Bloqueo de pantalla" value={sec.screen_lock_enabled != null ? (sec.screen_lock_enabled ? '\u2713 Activo' : '\u2717 Inactivo') : null} />
                  <Field label="Cifrado"             value={sec.encryption_status} />
                  <Field label="Depuraci\u00f3n USB"      value={sec.usb_debugging != null ? (sec.usb_debugging ? '\u26a0 Activo' : '\u2713 Inactivo') : null} />
                  <Field label="Opciones developer"  value={sec.developer_options != null ? (sec.developer_options ? '\u26a0 Activo' : '\u2713 Inactivo') : null} />
                  <Field label="Fuentes desconocidas"value={sec.unknown_sources != null ? (sec.unknown_sources ? '\u26a0 Permitido' : '\u2713 Bloqueado') : null} />
                  <Field label="Admin dispositivo"   value={md.is_device_admin != null ? (md.is_device_admin ? '\u2713 Activo' : '\u2014 Sin activar') : null} />
                  <Field label="Parche seguridad"    value={md.security_patch_level} />
                </div>

                {/* Telefon\u00eda */}
                {ph && (
                  <>
                    <div className="detail-section-title" style={{ marginTop: 20 }}>Telefon\u00eda y SIM</div>
                    <div className="fields-grid">
                      <Field label="N\u00famero"        value={ph.phone_number} />
                      <Field label="Operador"      value={ph.carrier} />
                      <Field label="Operador SIM"  value={ph.sim_operator} />
                      <Field label="Tipo de red"   value={ph.network_type} />
                      <Field label="Itinerancia"   value={ph.is_roaming ? '\u26a0 Roaming activo' : '\u2713 Sin roaming'} />
                    </div>
                  </>
                )}

                {/* Detalles del sistema */}
                <div className="detail-section-title" style={{ marginTop: 20 }}>Sistema</div>
                <div className="fields-grid">
                  <Field label="Versi\u00f3n Android"  value={md.android_version ? `Android ${md.android_version}` : null} />
                  <Field label="SDK Level"         value={md.sdk_level != null ? String(md.sdk_level) : null} />
                  <Field label="Build"             value={md.build_number} />
                  <Field label="CPU ABI"           value={md.cpu_abi} />
                  <Field label="Zona horaria"      value={md.timezone} />
                  <Field label="Idioma"            value={md.language} />
                  <Field label="WiFi SSID"         value={md.wifi_ssid} />
                  <Field label="Uptime"            value={md.uptime_hours != null ? `${Math.floor(md.uptime_hours)}h` : null} />
                  <Field label="Agente"            value={md.agent_version ? `v${md.agent_version}` : null} />
                </div>

              </>
            );
          })()}

          <div className="detail-section-title" style={{ marginTop: 20 }}>Seguridad</div>
          <div className="fields-grid">
            <Field label="Firewall" value={asset.firewall_enabled === null ? '\u2014' : asset.firewall_enabled ? '\u2713 Activo' : '\u2717 Inactivo'} />
            {(() => {
              const isLinux = /linux|ubuntu|debian|centos|rhel|fedora|arch|suse|alma|rocky/i.test(asset.os_name || '');
              return isLinux ? (<>
                <Field label="Root SSH" value={asset.root_ssh_enabled === null ? '\u2014' : asset.root_ssh_enabled ? '\u26a0 Habilitado' : '\u2713 Deshabilitado'} />
                <Field label="Sudo sin contrase\u00f1a" value={asset.sudo_nopasswd === null ? '\u2014' : asset.sudo_nopasswd ? '\u26a0 S\u00ed' : '\u2713 No'} />
                <Field label="Actualizaciones auto" value={asset.auto_updates_enabled === null ? '\u2014' : asset.auto_updates_enabled ? '\u2713 Activo' : '\u2717 Inactivo'} />
              </>) : (<>
                <Field label="Antivirus" value={asset.antivirus_name ? `${asset.antivirus_name} ${asset.antivirus_enabled ? '\u2713 Activo' : '\u2717 Inactivo'}` : '\u2014'} />
                <Field label="BitLocker" value={asset.bitlocker_enabled === null ? '\u2014' : asset.bitlocker_enabled ? '\u2713 Activo' : '\u2717 Inactivo'} />
                <Field label="UAC" value={asset.uac_enabled === null ? '\u2014' : asset.uac_enabled ? '\u2713 Activo' : '\u2717 Inactivo'} />
                <Field label="RDP" value={asset.rdp_enabled === null ? '\u2014' : asset.rdp_enabled ? '\u26a0 Habilitado' : '\u2713 Deshabilitado'} />
                <Field label="Admin local" value={asset.is_local_admin === null ? '\u2014' : asset.is_local_admin ? '\u26a0 Sí' : '\u2713 No'} />
              </>);
            })()}
          </div>


          {/* Actualizaciones pendientes */}
          {(() => {
            const updates = Array.isArray(asset.pending_updates)
              ? asset.pending_updates
              : (typeof asset.pending_updates === 'string' ? JSON.parse(asset.pending_updates || '[]') : []);
            if (updates.length === 0) return null;
            const SEVERITY_COLOR = {
              Critical:  { color: 'var(--red)', bg: 'var(--red-dim)', border: '#F5C0BC' },
              Important: { color: '#D45D00', bg: 'var(--amber-dim)', border: '#F5C080' },
              Moderate:  { color: 'var(--accent)', bg: '#E6F1FB', border: '#90C4E8' },
              Low:       { color: 'var(--ink-2)', bg: 'var(--bg-subtle)', border: '#DDDDDD' },
            };
            return (
              <div style={{ marginTop: 20 }}>
                <div className="detail-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  Actualizaciones pendientes
                  <span style={{ color: 'var(--red)', fontSize: 11, fontWeight: 700 }}>
                    {updates.length}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  {updates.map((u, i) => {
                    const sev = SEVERITY_COLOR[u.severity] || SEVERITY_COLOR.Low;
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--bg-subtle)', borderRadius: 8, border: '1px solid var(--border)' }}>
                        <span style={{ color: sev.color, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {u.severity || 'Low'}
                        </span>
                        {u.kb && (
                          <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {u.kb}
                          </span>
                        )}
                        <span style={{ fontSize: 12, color: 'var(--ink-2)', flex: 1 }}>{u.title}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <div className="detail-section-title" style={{ marginTop: 20 }}>Inventario</div>
          <div className="fields-grid">
            <Field label="Número de inventario" value={asset.inventory_number || 'Sin asignar'} />
            <Field label="Tipo de activo" value={asset.asset_type || '\u2014'} />
          </div>
        </div>
      )}

      {activeTab === 'red' && (
        <div className="card detail-card">
          <div className="detail-section-title">Direcciones IP</div>
          {ips.length === 0 ? <p className="empty-msg">Sin datos de red.</p> : (
            <table>
              <thead><tr><th>Direcci\u00f3n IP</th><th>Adaptador</th></tr></thead>
              <tbody>
                {ips.map((ip, i) => (
                  <tr key={i}>
                    <td><span className="mono">{ip.address || ip}</span></td>
                    <td style={{ color: 'var(--gray-500)' }}>{ip.adapter || '\u2014'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="detail-section-title" style={{ marginTop: 20 }}>Direcciones MAC</div>
          {macs.length === 0 ? <p className="empty-msg">Sin datos.</p> : (
            <div className="mac-list">
              {macs.map((m, i) => <span key={i} className="mac-badge mono">{m}</span>)}
            </div>
          )}

          {/* \u2500\u2500 WiFi \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */}
          <div className="detail-section-title" style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="ti ti-wifi" style={{ fontSize: 15, color: 'var(--accent)' }} />
            Redes WiFi
            {loadingWifi && <span style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 400 }}>cargando...</span>}
          </div>

          {!loadingWifi && wifiProfiles !== null && (
            <>
              {/* Red actualmente conectada */}
              {wifiProfiles.current_ssid && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--green-dim)', border: '1px solid var(--green-dim)', borderRadius: 8, marginBottom: 14 }}>
                  <span style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--green-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="ti ti-wifi" style={{ fontSize: 16, color: 'var(--green)' }} />
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)' }}>{wifiProfiles.current_ssid}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 1, display: 'flex', gap: 12 }}>
                      {wifiProfiles.current_bssid && <span className="mono">{wifiProfiles.current_bssid}</span>}
                      {wifiProfiles.current_signal != null && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <i className="ti ti-antenna-bars-4" style={{ fontSize: 12 }} />
                          {wifiProfiles.current_signal}%
                        </span>
                      )}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--green)',}}>Conectada</span>
                </div>
              )}

              {/* Perfiles guardados */}
              {wifiProfiles.saved_profiles?.length > 0 ? (
                <>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                    {wifiProfiles.saved_profiles.length} redes guardadas
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {wifiProfiles.saved_profiles.map((p, i) => {
                      const isConnected = p.ssid === wifiProfiles.current_ssid;
                      const authColor = /open|none/i.test(p.authentication || '') ? 'var(--red)'
                        : /wpa3/i.test(p.authentication || '') ? 'var(--green)'
                        : 'var(--accent)';
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: isConnected ? 'var(--green-dim)' : 'var(--bg-subtle)', border: `1px solid ${isConnected ? 'var(--green-dim)' : 'var(--border)'}`, borderRadius: 8 }}>
                          <i className={`ti ti-wifi${isConnected ? '' : '-off'}`} style={{ fontSize: 15, color: isConnected ? 'var(--green)' : 'var(--ink-3)', flexShrink: 0 }} />
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', flex: 1 }}>{p.ssid}</span>
                          {p.authentication && (
                            <span style={{ fontSize: 10, fontWeight: 600, color: authColor,}}>
                              {p.authentication}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="empty-msg">No se encontraron perfiles WiFi guardados.</p>
              )}

              {/* Redes visibles (para auditor\u00eda/geo futura) */}
              {wifiVisible?.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 20, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="ti ti-radar" style={{ fontSize: 12 }} />
                    {wifiVisible.length} redes visibles \u00b7 \u00faltimas 24h
                    <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--ink-3)', textTransform: 'none', letterSpacing: 0 }}>(BSSID para geolocalizaci\u00f3n)</span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ fontSize: 12, width: '100%' }}>
                      <thead>
                        <tr style={{ color: 'var(--ink-2)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          <th style={{ textAlign: 'left', paddingBottom: 6 }}>SSID</th>
                          <th style={{ textAlign: 'left', paddingBottom: 6 }}>BSSID</th>
                          <th style={{ textAlign: 'center', paddingBottom: 6 }}>Se\u00f1al</th>
                          <th style={{ textAlign: 'center', paddingBottom: 6 }}>Canal</th>
                          <th style={{ textAlign: 'left', paddingBottom: 6 }}>Auth</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wifiVisible.map((n, i) => (
                          <tr key={i} style={{ borderTop: '1px solid var(--bg)' }}>
                            <td style={{ padding: '6px 0', fontWeight: 500, color: 'var(--ink)' }}>{n.ssid || <span style={{ color: 'var(--ink-3)' }}>Oculta</span>}</td>
                            <td style={{ padding: '6px 8px' }}><span className="mono" style={{ fontSize: 11, color: 'var(--ink-2)' }}>{n.bssid}</span></td>
                            <td style={{ textAlign: 'center' }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: n.signal >= 70 ? 'var(--green)' : n.signal >= 40 ? 'var(--amber)' : 'var(--red)' }}>
                                {n.signal != null ? `${n.signal}%` : '\u2014'}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center', color: 'var(--ink-2)' }}>{n.channel || '\u2014'}</td>
                            <td style={{ fontSize: 10, color: 'var(--ink-2)' }}>{n.authentication || '\u2014'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}

          {!loadingWifi && wifiProfiles === null && (
            <p className="empty-msg">Sin datos WiFi \u2014 el agente v1.3.0 o superior los recolecta autom\u00e1ticamente.</p>
          )}
        </div>
      )}

      {activeTab === 'software' && (
        <div className="card detail-card">
          <div className="detail-section-title">
            Software instalado ({asset.software?.length || 0} aplicaciones)
            {(() => {
              const u = (asset.software || []).filter(s => s.source === 'user').length;
              const p = (asset.software || []).filter(s => s.source === 'process').length;
              if (!u && !p) return null;
              return (
                <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--ink-3)', marginLeft: 8 }}>
                  {u > 0 && <span style={{ color: '#7c3aed' }}>· {u} por usuario</span>}
                  {p > 0 && <span style={{ color: '#0891b2', marginLeft: 6 }}>· {p} por proceso</span>}
                </span>
              );
            })()}
          </div>
          {!asset.software?.length ? <p className="empty-msg">Sin datos de software.</p> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Nombre</th><th>Versioon</th><th>Publisher</th><th>Origen</th></tr></thead>
                <tbody>
                  {asset.software.map(sw => {
                    const info = swCveMap[sw.name];
                    const sev = info?.sev;
                    const isCve = info?.type === 'cve';
                    const rowStyle = sev === 'critical' ? { background: 'var(--red-dim)' }
                                   : sev === 'high'     ? { background: 'var(--amber-dim)' }
                                   : sev === 'medium'   ? { background: 'var(--amber-dim)' }
                                   : {};
                    const BADGES = {
                      critical: { label: isCve ? 'CVE Cr\u00edtico' : 'Riesgo Cr\u00edtico', color: 'var(--red)', bg: 'var(--red-dim)' },
                      high:     { label: isCve ? 'CVE Alto'    : 'Riesgo Alto',    color: 'var(--amber)', bg: 'var(--amber-dim)' },
                      medium:   { label: isCve ? 'CVE Medio'   : 'Riesgo Medio',   color: 'var(--amber)', bg: 'var(--amber-dim)' },
                    };
                    const badge = sev ? BADGES[sev] : null;
                    return (
                      <tr key={sw.id} style={rowStyle}>
                        <td style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {sw.name}
                          {badge && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: badge.color, whiteSpace: 'nowrap' }}>
                              {badge.label}
                            </span>
                          )}
                        </td>
                        <td><span className="mono" style={{ fontSize: 12 }}>{sw.version || '\u2014'}</span></td>
                        <td style={{ color: 'var(--gray-500)' }}>{sw.publisher || '\u2014'}</td>
                        <td>{
                          sw.source === 'user'    ? <span style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>Usuario</span>
                        : sw.source === 'process' ? <span style={{ fontSize: 11, fontWeight: 700, color: '#0891b2' }}>Proceso</span>
                        : <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>M\u00e1quina</span>
                        }</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'apps' && (() => {
        const md = asset.agent_metadata || {};
        const apps = Array.isArray(md.installed_apps) ? md.installed_apps : [];
        return (
          <div className="card detail-card">
            <div className="detail-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Apps instaladas
              <span style={{ color: 'var(--accent)', fontSize: 11, fontWeight: 700 }}>
                {apps.length}
              </span>
            </div>
            {!apps.length ? <p className="empty-msg">Sin datos de apps.</p> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12 }}>
                {apps.map((app, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', background: 'var(--bg-subtle)', borderRadius: 6, border: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 13, color: 'var(--ink-2)', flex: 1, fontWeight: 500 }}>{app.app_name}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--ink-2)' }}>{app.version}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{app.install_date}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {activeTab === 'costos' && (
        <div className="card detail-card">
          {(user?.role === 'admin' || user?.role === 'manager') && asset.asset_type === 'physical' && (
            <MarketPricePanel asset={asset} />
          )}
          <CostsTab assetId={asset.id} />
        </div>
      )}

      {activeTab === 'historial' && (
        <div className="card detail-card">
          <div className="detail-section-title">Historial de cambios</div>
          {(user?.role === 'admin' || user?.role === 'technician') && (
            <div className="note-form">
              <div className="detail-section-title" style={{marginBottom:8}}>Agregar nota manual</div>
              <textarea className="form-input" rows={3}
                placeholder="Ej: Juan Pérez se desvinculó el 01/06/2026. Equipo reasignado a María Gómez."
                value={note} onChange={e => setNote(e.target.value)}
                style={{resize:'vertical', fontFamily:'inherit', marginBottom:8, width:'100%', display:'block', boxSizing:'border-box'}} />
              <button className="btn btn-primary btn-sm" onClick={handleAddNote} disabled={addingNote}>
                {addingNote ? 'Guardando...' : '+ Agregar nota'}
              </button>
              {noteSuccess && <div style={{color:'var(--green)', fontSize:13, marginTop:6}}>\u2713 {noteSuccess}</div>}
            </div>
          )}
          {!asset.scan_history?.length ? <p className="empty-msg">Sin historial.</p> : (
            <div className="history-list">
              {asset.scan_history.map(h => (
                <div key={h.scan_token} className="history-item">
                  <div className="history-header">
                    <div style={{display:'flex', alignItems:'center', gap:8}}>
                      {changeTypeLabel(h.change_type)}
                      <span style={{fontSize:12, color:'var(--gray-500)'}}>{new Date(h.scanned_at).toLocaleString('es-AR')}</span>
                      {h.agent_version && (
                        <span style={{fontSize:11, fontWeight:600, fontFamily:'monospace', color:'var(--ink-2)',}}>
                          v{h.agent_version}
                        </span>
                      )}
                    </div>
                  </div>
                  {h.change_type === 'user_change' && (
                    <div className="history-body">
                      <span style={{fontSize:13}}><strong>Antes:</strong> {h.previous_user || '\u2014'} {h.previous_email && <span style={{color:'var(--gray-500)'}}>({h.previous_email})</span>}</span>
                      <span style={{color:'var(--gray-500)', margin:'0 8px'}}>â†’</span>
                      <span style={{fontSize:13}}><strong>Ahora:</strong> {h.new_user || '\u2014'} {h.new_email && <span style={{color:'var(--blue)'}}>({h.new_email})</span>}</span>
                    </div>
                  )}
                  {h.change_type === 'initial_registration' && (
                    <div className="history-body">
                      <span style={{fontSize:13}}>Registrado por <strong>{h.new_user}</strong> {h.new_email && <span style={{color:'var(--blue)'}}>({h.new_email})</span>}</span>
                    </div>
                  )}
                  {h.change_type === 'admin_note' && (
                    <div className="history-body" style={{background:'var(--blue-light)', borderRadius:6, padding:'10px 12px'}}>
                      <div style={{fontSize:13, color:'var(--gray-900)'}}>{h.admin_note}</div>
                      <div style={{fontSize:11, color:'var(--gray-500)', marginTop:4}}>\u2014 {h.admin_user}</div>
                    </div>
                  )}
                  {h.change_type === 'update' && (
                    <div className="history-body">
                      <span style={{fontSize:13, color:'var(--gray-500)'}}>Datos actualizados por <strong>{h.new_user || '\u2014'}</strong></span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'incidentes' && (
        <div className="card detail-card">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div className="detail-section-title">Incidentes vinculados</div>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/incidents')}>+ Crear incidente</button>
          </div>
          {loadingIncidents ? <div className="spinner" style={{margin:'20px auto'}} /> :
           assetIncidents.length === 0 ? <p className="empty-msg">Sin incidentes vinculados a este equipo.</p> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>Titulo</th><th>Prioridad</th><th>Estado</th><th>Fecha</th><th></th></tr></thead>
                <tbody>
                  {assetIncidents.map(inc => {
                    const priColors = { critica:['#FDECEB','#C0392B'], alta:['#FFF3E8','#D45D00'], media:['#F1EFE8','#5A5A5A'], baja:['#EAF4ED','#1A6E2E'] };
                    const stColors = { nuevo:['#E6F1FB','#0063A5'], en_progreso:['#FFF3E8','#D45D00'], pendiente:['#F1EFE8','#5A5A5A'], resuelto:['#EAF4ED','#1A6E2E'], cerrado:['#F1EFE8','#5A5A5A'] };
                    const [pb,pc] = priColors[inc.priority]||['#F1EFE8','#5A5A5A'];
                    const [sb,sc] = stColors[inc.status]||['#F1EFE8','#5A5A5A'];
                    return (
                      <tr key={inc.id}>
                        <td><span className="mono" style={{fontSize:11,color:'var(--gray-500)'}}>#{inc.ticket_number}</span></td>
                        <td style={{fontSize:13,fontWeight:500}}>{inc.title}</td>
                        <td><span style={{color:pc,fontSize:11,fontWeight:500}}>{inc.priority}</span></td>
                        <td><span style={{color:sc,fontSize:11,fontWeight:500}}>{inc.status}</span></td>
                        <td style={{fontSize:12,color:'var(--gray-500)'}}>{new Date(inc.created_at).toLocaleDateString('es-AR')}</td>
                        <td><Link to={`/incidents/${inc.id}`} className="btn btn-secondary btn-sm">Ver</Link></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'criticidad' && criticidadForm && (
        <div className="card detail-card">
          <div className="detail-section-title" style={{ marginBottom: 20 }}>Matriz de criticidad</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Ambiente */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>Ambiente</label>
              <select
                value={criticidadForm.ambiente}
                onChange={e => setCriticidadForm(f => ({ ...f, ambiente: e.target.value }))}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-2)', fontSize: 13 }}>
                <option value="">— Sin definir —</option>
                <option value="produccion">Producción</option>
                <option value="staging">Staging</option>
                <option value="desarrollo">Desarrollo</option>
                <option value="testing">Testing</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            {/* Criticidad */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>Criticidad</label>
              <select
                value={criticidadForm.criticidad}
                onChange={e => setCriticidadForm(f => ({ ...f, criticidad: e.target.value }))}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-2)', fontSize: 13,
                  fontWeight: 600,
                  color: criticidadForm.criticidad === 'critica' ? 'var(--red)' : criticidadForm.criticidad === 'alta' ? '#D45D00' : criticidadForm.criticidad === 'media' ? 'var(--ink-2)' : criticidadForm.criticidad === 'baja' ? 'var(--green)' : 'var(--ink-2)',
                }}>
                <option value="">— Sin definir —</option>
                <option value="critica">🔴 Crítica</option>
                <option value="alta">🟠 Alta</option>
                <option value="media">🟡 Media</option>
                <option value="baja">🟢 Baja</option>
              </select>
            </div>
            {/* Disponibilidad del servicio */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>Disponibilidad del servicio</label>
              <input
                type="text" placeholder="Ej: 24x7, 8x5, 16x5"
                value={criticidadForm.disponibilidad_servicio}
                onChange={e => setCriticidadForm(f => ({ ...f, disponibilidad_servicio: e.target.value }))}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-2)', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            {/* Horario crítico */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>Horario crítico</label>
              <input
                type="text" placeholder="Ej: Lunes a viernes 8-20hs"
                value={criticidadForm.horario_critico}
                onChange={e => setCriticidadForm(f => ({ ...f, horario_critico: e.target.value }))}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-2)', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            {/* RTO */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>RTO (horas para normalizar)</label>
              <input
                type="number" min="0" placeholder="Ej: 4"
                value={criticidadForm.rto_horas}
                onChange={e => setCriticidadForm(f => ({ ...f, rto_horas: e.target.value }))}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-2)', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            {/* Booleanos */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={!!criticidadForm.drp}
                  onChange={e => setCriticidadForm(f => ({ ...f, drp: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: 'pointer' }} />
                <span><strong>DRP</strong> — Incluido en plan de recuperación</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={!!criticidadForm.alta_disponibilidad}
                  onChange={e => setCriticidadForm(f => ({ ...f, alta_disponibilidad: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: 'pointer' }} />
                <span><strong>Alta disponibilidad</strong> — Redundancia activa</span>
              </label>
            </div>
            {/* Impacto en el negocio — ocupa toda la fila */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 4 }}>Impacto en el negocio</label>
              <textarea
                rows={3} placeholder="Describí el impacto que tendría la caída de este activo..."
                value={criticidadForm.impacto_negocio}
                onChange={e => setCriticidadForm(f => ({ ...f, impacto_negocio: e.target.value }))}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-2)', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
          </div>
          {(user?.role === 'admin' || user?.role === 'technician') && (
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-primary"
                onClick={handleCriticidadSave}
                disabled={savingCriticidad}>
                {savingCriticidad ? 'Guardando...' : 'Guardar criticidad'}
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'usb' && (
        <div className="card detail-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div className="detail-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
              <HardDrive size={16} /> Dispositivos USB
            </div>
            {/* Bloqueo USB — disponible en todos los activos físicos, solo admin */}
            {asset.asset_type === 'physical' && hasRole(user, 'admin') && (
              <UsbBlockControl asset={asset} />
            )}
          </div>
          <UsbTab assetId={asset.id} />
        </div>
      )}

      {activeTab === 'salud' && (
        <div>
          <HwHealthTab asset={asset} />
        </div>
      )}

      {activeTab === 'comandos' && (
        <div className="card detail-card">
          <CommandsTab asset={asset} user={user} />
        </div>
      )}

      {activeTab === 'politicas' && (
        <div className="card detail-card">
          <PolicyComplianceTab asset={asset} user={user} onAuditComplete={loadAsset} />
        </div>
      )}
    </div>
  );
}



